import React, { useEffect, useState, useRef } from 'react';
import { 
    Form, Input, Button, Upload, message, Avatar, Tag, Spin, Empty, 
    Badge, Tooltip, Table, Space, Popconfirm, DatePicker, Modal, Descriptions,
    Select, Row, Col, Alert, InputNumber
} from 'antd';
import {
    UploadOutlined, UserOutlined, LockOutlined, WarningOutlined, PlusOutlined,
    SafetyCertificateOutlined, BookOutlined, CarOutlined, EditOutlined,
    PhoneOutlined, DeleteOutlined, CloseOutlined, FileTextOutlined,
    TrophyOutlined, CalendarOutlined, IdcardOutlined, MailOutlined,
    GlobalOutlined, DesktopOutlined, EyeOutlined,
    CheckCircleOutlined, HomeOutlined, EnvironmentOutlined, CrownOutlined,
    SolutionOutlined, AuditOutlined, InfoCircleOutlined, CopyOutlined,
    SaveOutlined, FilePdfOutlined
} from '@ant-design/icons';
import { IUser } from '../../../../types/user.types';
import { uploadSignature, updateUser, uploadAvatar } from '../../../../apis/users';
import { optimizeImageBeforeUpload } from '../../../../utils/imageOptimizer';
import { getToken } from '../../../../utils/auth';
import { getBackendImageUrl } from '../../../../utils/imageUrl';
import QualificationModal from '../QualificationModal';
import CertificateModal from '../CertificateModal';
import certificatesApi from '../../../../apis/certificates';
import { IEmployeeCertificate } from '../../../../types/certificates.types';
import dayjs from 'dayjs';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { createProfileRequest, getMyRequests, IProfileRequest } from '../../../../apis/profileRequests';
import { createLaborContract } from '../../../../apis/laborContracts';
import { createRewardDiscipline } from '../../../../apis/rewardDisciplines';
import { LEAVE_TYPE_CONFIG, LeaveType, recordLeave, uploadLeaveAttachments, getAnnualLeavesByUser, getLeaveHistoriesByUser } from '../../../../apis/annualLeaves';
import { IUserTaskSummary } from '../../../../types/tasks.types';

// ─────────────────────────── Types ───────────────────────────
interface ProfileModalProps {
    open: boolean;
    onCancel: () => void;
    user: IUser | null;
    onUpdateSuccess: (updatedUser: IUser) => void;
    initialSection?: SectionKey;
}

// ─────────────────────────── Helpers ─────────────────────────
const getRoleLabel = (role: string | undefined) => {
    const map: Record<string, string> = {
        ADMIN: 'Quản trị viên', OPERATOR: 'Vận hành', TECHNICIAN: 'Kỹ thuật',
        TEAM_LEAD: 'Tổ trưởng', UNIT_HEAD: 'Cán bộ đội', DIRECTOR: 'Ban giám đốc',
        HR_MANAGER: 'Quản lý nhân sự',
    };
    return map[role?.toUpperCase() || ''] || role;
};

type SectionKey = 'info' | 'certificates' | 'cccm' | 'contracts' | 'history' | 'leaves' | 'tasks_kpi' | 'my_requests';

const NAV_ITEMS: { key: SectionKey; label: string; icon: React.ReactNode }[] = [
    { key: 'info', label: 'Thông tin cá nhân', icon: <UserOutlined /> },
    { key: 'certificates', label: 'Bằng cấp & GPLX', icon: <SafetyCertificateOutlined /> },
    { key: 'cccm', label: 'CCCM & Đào tạo', icon: <BookOutlined /> },
    { key: 'contracts', label: 'Hợp đồng lao động', icon: <FileTextOutlined /> },
    { key: 'history', label: 'Khen thưởng / Kỷ luật', icon: <TrophyOutlined /> },
    { key: 'leaves', label: 'Phép năm', icon: <CalendarOutlined /> },
    { key: 'tasks_kpi', label: 'Hiệu suất & Công việc', icon: <AuditOutlined /> },
    { key: 'my_requests', label: 'Lịch sử yêu cầu', icon: <IdcardOutlined /> },
];

// ─────────────────────── Main Component ──────────────────────
const ProfileModal: React.FC<ProfileModalProps> = ({ open, onCancel, user, onUpdateSuccess, initialSection }) => {
    const { user: currentUser, setUser: setAuthUser } = useAuthContext();

    const [infoForm] = Form.useForm();
    const [passForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState<SectionKey>('info');
    const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
    const [signatureUrl, setSignatureUrl] = useState<string | undefined>(undefined);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);

    const isOwnProfile = currentUser?.user_id === user?.user_id;
    const isPublicView = currentUser?.role === 'TEAM_LEAD' && !isOwnProfile;
    const isAdminOrHR = ['ADMIN', 'HR_MANAGER'].includes(currentUser?.role || '');

    // ── Data States ──
    const [qualifications, setQualifications] = useState<IEmployeeCertificate[]>([]);
    const [qualLoading, setQualLoading] = useState(false);
    const [qualModalOpen, setQualModalOpen] = useState(false);
    const [selectedQual, setSelectedQual] = useState<IEmployeeCertificate | null>(null);
    const [qualModalLoading, setQualModalLoading] = useState(false);

    const [cccmCerts, setCccmCerts] = useState<IEmployeeCertificate[]>([]);
    const [cccmLoading, setCccmLoading] = useState(false);

    const [laborContracts, setLaborContracts] = useState<any[]>([]);
    const [rewardDisciplines, setRewardDisciplines] = useState<any[]>([]);
    const [annualLeaves, setAnnualLeaves] = useState<any[]>([]);
    const [leaveHistories, setLeaveHistories] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [trainingPrograms, setTrainingPrograms] = useState<any[]>([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [isCertModalOpen, setIsCertModalOpen] = useState(false);
    const [selectedCert, setSelectedCert] = useState<any>(null);
    const [editCertificates, setEditCertificates] = useState<any[]>([]);

    const [myRequests, setMyRequests] = useState<IProfileRequest[]>([]);
    const [taskSummary, setTaskSummary] = useState<IUserTaskSummary | null>(null);

    const [loadedTabs, setLoadedTabs] = useState<Record<string, boolean>>({});

    // ── Add Modal States ──
    const [addContractOpen, setAddContractOpen] = useState(false);
    const [addRewardOpen, setAddRewardOpen] = useState(false);
    const [addModalLoading, setAddModalLoading] = useState(false);
    const [contractForm] = Form.useForm();
    const [rewardForm] = Form.useForm();
    const [recordLeaveOpen, setRecordLeaveOpen] = useState(false);
    const [recordLeaveLoading, setRecordLeaveLoading] = useState(false);
    const [recordLeaveForm] = Form.useForm();
    const [leaveFileList, setLeaveFileList] = useState<any[]>([]);

    // ── Data Loading ──
    useEffect(() => {
        if (open && trainingPrograms.length === 0) {
            certificatesApi.getTrainingPrograms()
                .then(data => setTrainingPrograms(data))
                .catch(err => console.error("Error fetching programs:", err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const prevOpenRef = useRef(false);
    const prevUserIdRef = useRef<number | null>(null);
    const loadedTabsRef = useRef<Record<string, boolean>>({});

    useEffect(() => {
        if (!open || !user) {
            prevOpenRef.current = false;
            return;
        }

        const isJustOpened = !prevOpenRef.current && open;
        const isDifferentUser = prevUserIdRef.current !== user.user_id;

        if (isJustOpened || isDifferentUser) {
            prevOpenRef.current = true;
            prevUserIdRef.current = user.user_id;
            loadedTabsRef.current = {};
            setLoadedTabs({});
            passForm.resetFields();
            setActiveSection(initialSection || 'info');
            fetchQuals(user.user_id);
            fetchCccmCerts(user.user_id);
        }

        // Cập nhật giá trị form và chữ ký nếu có cập nhật từ cha mà không reset tab
        infoForm.setFieldsValue({
            name: user.name,
            phone_number: user.phone_number,
            citizen_identification_card: user.citizen_identification_card,
            employee_code: user.employee_code,
            position: user.position,
            date_of_birth: user.date_of_birth ? dayjs(user.date_of_birth) : null,
            place_of_birth: user.place_of_birth,
            gender: user.gender,
            ethnicity: user.ethnicity,
            is_party_member: Boolean(user.is_party_member),
            cccd_issue_date: user.cccd_issue_date ? dayjs(user.cccd_issue_date) : null,
            permanent_address: user.permanent_address,
            temporary_address: user.temporary_address,
            hometown: user.hometown,
        });
        setAvatarUrl(user.avatar);
        setSignatureUrl(user.signature_url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user?.user_id, user?.avatar, user?.signature_url, initialSection]);

    // Lazy-load data per section mà không trigger re-render cycle
    useEffect(() => {
        if (!open || !user) return;
        const loadData = async () => {
            if (activeSection === 'contracts' && !loadedTabsRef.current.contracts) {
                loadedTabsRef.current.contracts = true;
                setHistoryLoading(true);
                try {
                    const { getLaborContractsByUser } = await import('../../../../apis/laborContracts');
                    const data = await getLaborContractsByUser(user.user_id).catch(() => []);
                    setLaborContracts(data);
                    setLoadedTabs(prev => ({ ...prev, contracts: true }));
                } finally { setHistoryLoading(false); }
            }
            if (activeSection === 'history' && !loadedTabsRef.current.history) {
                loadedTabsRef.current.history = true;
                setHistoryLoading(true);
                try {
                    const { getRewardDisciplinesByUser } = await import('../../../../apis/rewardDisciplines');
                    const data = await getRewardDisciplinesByUser(user.user_id).catch(() => []);
                    setRewardDisciplines(data);
                    setLoadedTabs(prev => ({ ...prev, history: true }));
                } finally { setHistoryLoading(false); }
            }
            if (activeSection === 'leaves' && !loadedTabsRef.current.leaves) {
                loadedTabsRef.current.leaves = true;
                setHistoryLoading(true);
                try {
                    const { getAnnualLeavesByUser, getLeaveHistoriesByUser } = await import('../../../../apis/annualLeaves');
                    const [leavesData, historiesData] = await Promise.all([
                        getAnnualLeavesByUser(user.user_id).catch(() => []),
                        getLeaveHistoriesByUser(user.user_id).catch(() => [])
                    ]);
                    setAnnualLeaves(leavesData);
                    setLeaveHistories(historiesData);
                    setLoadedTabs(prev => ({ ...prev, leaves: true }));
                } finally { setHistoryLoading(false); }
            }
            if (activeSection === 'cccm' && !loadedTabsRef.current.cccm) {
                loadedTabsRef.current.cccm = true;
                setHistoryLoading(true);
                try {
                    const data = await certificatesApi.getUserCertificates(user.user_id, 'CCCM');
                    setEditCertificates(data);
                    setCccmCerts(data);
                    setLoadedTabs(prev => ({ ...prev, cccm: true }));
                } finally { setHistoryLoading(false); }
            }
            if (activeSection === 'my_requests' && !loadedTabsRef.current.my_requests) {
                loadedTabsRef.current.my_requests = true;
                setHistoryLoading(true);
                try {
                    const data = await getMyRequests().catch(() => []);
                    setMyRequests(data);
                    setLoadedTabs(prev => ({ ...prev, my_requests: true }));
                } finally { setHistoryLoading(false); }
            }
            if (activeSection === 'tasks_kpi' && !loadedTabsRef.current.tasks_kpi) {
                loadedTabsRef.current.tasks_kpi = true;
                setHistoryLoading(true);
                try {
                    const { getUserTaskSummary } = await import('../../../../apis/tasks');
                    const data = await getUserTaskSummary(user.user_id).catch(() => null);
                    setTaskSummary(data);
                    setLoadedTabs(prev => ({ ...prev, tasks_kpi: true }));
                } finally { setHistoryLoading(false); }
            }
        };
        loadData();
    }, [activeSection, open, user]);

    const fetchQuals = async (targetUserId?: number) => {
        const uid = targetUserId || user?.user_id;
        if (!uid) return;
        setQualLoading(true);
        try {
            const data = await certificatesApi.getUserCertificates(uid, 'BANG_CAP');
            setQualifications(data);
        } catch { message.error('Lỗi lấy danh sách chứng chỉ/bằng cấp'); }
        finally { setQualLoading(false); }
    };

    const fetchCccmCerts = async (targetUserId?: number) => {
        const uid = targetUserId || user?.user_id;
        if (!uid) return;
        setCccmLoading(true);
        try {
            const data = await certificatesApi.getUserCertificates(uid, 'CCCM');
            setCccmCerts(data);
            setEditCertificates(data);
        } catch { message.error('Lỗi lấy danh sách CCCM'); }
        finally { setCccmLoading(false); }
    };

    // ── Handlers ──

    // Refresh contract data
    const refreshContracts = async () => {
        if (!user) return;
        try {
            const { getLaborContractsByUser } = await import('../../../../apis/laborContracts');
            const data = await getLaborContractsByUser(user.user_id).catch(() => []);
            setLaborContracts(data);
            loadedTabsRef.current.contracts = true;
            setLoadedTabs(prev => ({ ...prev, contracts: true }));
        } catch { /* silent */ }
    };

    // Refresh reward/discipline data
    const refreshRewards = async () => {
        if (!user) return;
        try {
            const { getRewardDisciplinesByUser } = await import('../../../../apis/rewardDisciplines');
            const data = await getRewardDisciplinesByUser(user.user_id).catch(() => []);
            setRewardDisciplines(data);
            loadedTabsRef.current.history = true;
            setLoadedTabs(prev => ({ ...prev, history: true }));
        } catch { /* silent */ }
    };

    const handleAddContract = async (values: any) => {
        if (!user) return;
        setAddModalLoading(true);
        try {
            await createLaborContract({
                ...values,
                user: user.user_id,
                start_date: values.start_date?.format('YYYY-MM-DD'),
                end_date: values.end_date?.format('YYYY-MM-DD'),
            });
            message.success('Th\u00eam h\u1ee3p \u0111\u1ed3ng th\u00e0nh c\u00f4ng');
            setAddContractOpen(false);
            contractForm.resetFields();
            refreshContracts();
            onUpdateSuccess(user);
        } catch (err: any) {
            message.error(err.message || 'L\u1ed7i th\u00eam h\u1ee3p \u0111\u1ed3ng');
        } finally {
            setAddModalLoading(false);
        }
    };

    const handleAddReward = async (values: any) => {
        if (!user) return;
        setAddModalLoading(true);
        try {
            await createRewardDiscipline({
                ...values,
                user: user.user_id,
                effective_date: values.effective_date?.format('YYYY-MM-DD'),
            });
            message.success('Th\u00eam quy\u1ebft \u0111\u1ecbnh th\u00e0nh c\u00f4ng');
            setAddRewardOpen(false);
            rewardForm.resetFields();
            refreshRewards();
        } catch (err: any) {
            message.error(err.message || 'L\u1ed7i th\u00eam quy\u1ebft \u0111\u1ecbnh');
        } finally {
            setAddModalLoading(false);
        }
    };

    const reloadLeavesData = async () => {
        if (!user?.user_id) return;
        setHistoryLoading(true);
        try {
            const [leavesData, historiesData] = await Promise.all([
                getAnnualLeavesByUser(user.user_id).catch(() => []),
                getLeaveHistoriesByUser(user.user_id).catch(() => [])
            ]);
            setAnnualLeaves(leavesData);
            setLeaveHistories(historiesData);
            loadedTabsRef.current.leaves = true;
            setLoadedTabs(prev => ({ ...prev, leaves: true }));
        } catch { /* silent */ }
        finally { setHistoryLoading(false); }
    };

    const handleOpenRecordLeave = () => {
        recordLeaveForm.resetFields();
        recordLeaveForm.setFieldsValue({
            leave_type: 'ANNUAL',
            days: 1,
            date_range: [dayjs(), dayjs()],
        });
        setLeaveFileList([]);
        setRecordLeaveOpen(true);
    };

    const handleDateRangeChangeInProfile = (dates: any) => {
        if (dates && dates[0] && dates[1]) {
            const diffDays = dates[1].startOf('day').diff(dates[0].startOf('day'), 'day') + 1;
            if (diffDays > 0) {
                recordLeaveForm.setFieldsValue({ days: diffDays });
            }
        }
    };

    const handleRecordLeaveSubmitInProfile = async () => {
        if (!user?.user_id) return;
        try {
            const values = await recordLeaveForm.validateFields();
            setRecordLeaveLoading(true);

            let attachments: string[] = [];
            if (leaveFileList.length > 0) {
                const filesToUpload = leaveFileList
                    .map((f: any) => f.originFileObj || f)
                    .filter((f: any) => f instanceof File);

                if (filesToUpload.length > 0) {
                    try {
                        attachments = await uploadLeaveAttachments(filesToUpload);
                    } catch (err: any) {
                        message.warning('Tải file đính kèm thất bại, tiếp tục ghi nhận thông tin phép');
                    }
                }
            }

            const [startDate, endDate] = values.date_range;
            await recordLeave({
                user_id: user.user_id,
                year: startDate.year(),
                applied_month: startDate.month() + 1,
                leave_type: values.leave_type || 'ANNUAL',
                days_taken: Number(values.days),
                from_date: startDate.format('YYYY-MM-DD'),
                to_date: endDate.format('YYYY-MM-DD'),
                reason: values.reason,
                attachments,
            });

            message.success('Ghi nhận nghỉ phép thành công!');
            setRecordLeaveOpen(false);
            setLeaveFileList([]);
            await reloadLeavesData();
            if (user) {
                onUpdateSuccess(user);
            }
        } catch (error: any) {
            console.error('Error recording leave in profile:', error);
            message.error(error.response?.data?.message || 'Có lỗi xảy ra khi ghi nhận nghỉ phép');
        } finally {
            setRecordLeaveLoading(false);
        }
    };

    const leaveUploadProps = {
        onRemove: (file: any) => {
            const index = leaveFileList.indexOf(file);
            const newFileList = leaveFileList.slice();
            newFileList.splice(index, 1);
            setLeaveFileList(newFileList);
        },
        beforeUpload: (file: File) => {
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            const isImage = file.type.startsWith('image/');
            if (!isPdf && !isImage) {
                message.error('Chỉ hỗ trợ file PDF hoặc ảnh (JPG, PNG)');
                return Upload.LIST_IGNORE;
            }
            if (file.size > 10 * 1024 * 1024) {
                message.error('Dung lượng file tối đa là 10MB');
                return Upload.LIST_IGNORE;
            }
            setLeaveFileList([file]);
            return false;
        },
        fileList: leaveFileList,
        maxCount: 1,
    };


    const handleChangePassword = async (values: any) => {
        if (!user) return;
        setLoading(true);
        try {
            const token = getToken();
            await updateUser(user.user_id, { password: values.newPassword }, token);
            message.success('Đổi mật khẩu thành công!');
            passForm.resetFields();
            setPasswordModalOpen(false);
        } catch (error: any) { message.error(error.message || 'Đổi mật khẩu thất bại'); }
        finally { setLoading(false); }
    };

    const handleAddQual = () => { setSelectedQual(null); setQualModalOpen(true); };
    const handleEditQual = (cert: IEmployeeCertificate) => { setSelectedQual(cert); setQualModalOpen(true); };
    const handleDeleteQual = async (id: number) => {
        try {
            await certificatesApi.deleteCertificate(id);
            message.success('Đã xóa');
            setQualifications(prev => prev.filter(q => q.id !== id));
        } catch (error: any) { message.error(error.message || 'Lỗi khi xóa'); }
    };

    const handleQualModalOk = async (values: any, files?: File[]) => {
        if (!user || !currentUser) return;
        if (files && files.some(f => f.size / 1024 / 1024 >= 5)) {
            message.error('File đính kèm phải nhỏ hơn 5MB!');
            return;
        }
        setQualModalLoading(true);
        try {
            if (isAdminOrHR) {
                if (selectedQual) {
                    const res = await certificatesApi.updateCertificate(selectedQual.id, values, files);
                    setQualifications(prev => prev.map(q => q.id === selectedQual.id ? res : q));
                    message.success('Cập nhật thành công');
                } else {
                    const res = await certificatesApi.createCertificate(user.user_id, values, files);
                    setQualifications(prev => [...prev, res]);
                    message.success('Thêm mới thành công');
                }
            } else {
                await createProfileRequest({ request_type: 'NEW_CERTIFICATE', data_payload: values }, files);
                message.success('Yêu cầu đã được gửi. Vui lòng chờ HR phê duyệt.');
            }
            setQualModalOpen(false);
        } catch (error: any) { message.error(error.message || 'Lỗi lưu thông tin'); }
        finally { setQualModalLoading(false); }
    };

    const handleAddCert = () => { setSelectedCert(null); setIsCertModalOpen(true); };
    const handleEditCert = (cert: any) => { setSelectedCert(cert); setIsCertModalOpen(true); };

    const handleDeleteCert = async (id: number) => {
        try {
            await certificatesApi.deleteCertificate(id);
            message.success('Đã xóa chứng chỉ');
            setEditCertificates(prev => prev.filter(c => c.id !== id));
            fetchCccmCerts();
        } catch (error: any) { message.error(error.message || 'Lỗi khi xóa chứng chỉ'); }
    };

    const handleCertModalOk = async (values: any, files?: File[]) => {
        if (!user) return;
        if (files && files.some(f => f.size / 1024 / 1024 >= 5)) {
            message.error('Các file đính kèm phải nhỏ hơn 5MB!');
            return;
        }
        setModalLoading(true);
        try {
            if (selectedCert) {
                const res = await certificatesApi.updateCertificate(selectedCert.id, values, files);
                setEditCertificates(prev => prev.map(c => c.id === selectedCert.id ? res : c));
                message.success('Cập nhật thành công');
            } else {
                if (isAdminOrHR) {
                    const res = await certificatesApi.createCertificate(user.user_id, values, files);
                    setEditCertificates(prev => [...prev, res]);
                    message.success('Thêm mới thành công');
                } else {
                    await createProfileRequest({ request_type: 'NEW_CERTIFICATE', data_payload: values }, files);
                    message.success('Yêu cầu đã được gửi. Vui lòng chờ HR phê duyệt.');
                }
            }
            fetchCccmCerts();
            setIsCertModalOpen(false);
        } catch (error: any) { message.error(error.message || 'Lỗi lưu thông tin'); }
        finally { setModalLoading(false); }
    };

    // ── Computed ──
    const getExpiryStatus = (cert: IEmployeeCertificate): 'ok' | 'warning' | 'expired' | 'permanent' => {
        if (cert.is_permanent) return 'permanent';
        if (!cert.expiry_date) return 'ok';
        const d = dayjs(cert.expiry_date);
        if (d.isBefore(dayjs())) return 'expired';
        if (d.diff(dayjs(), 'month', true) <= 3) return 'warning';
        return 'ok';
    };

    const getCccmExpiryStatus = (cert: IEmployeeCertificate): 'ok' | 'warning' | 'expired' => {
        const endDate = cert.end_date || cert.expiry_date;
        if (!endDate) return 'ok';
        const d = dayjs(endDate);
        if (d.isBefore(dayjs())) return 'expired';
        if (d.diff(dayjs(), 'month', true) <= 3) return 'warning';
        return 'ok';
    };

    if (!user) return null;

    const degrees = qualifications.filter(c => c.qualification_type === 'BANG_CAP' || (!c.qualification_type && !c.license_class));
    const licenses = qualifications.filter(c => c.qualification_type === 'GIAY_PHEP_LAI_XE' || (!c.qualification_type && c.license_class));
    const englishCerts = qualifications.filter(c => c.qualification_type === 'CHUNG_CHI_NGOAI_NGU');
    const itCerts = qualifications.filter(c => c.qualification_type === 'CHUNG_CHI_TIN_HOC');
    const expiringCount = licenses.filter(c => { const s = getExpiryStatus(c); return s === 'warning' || s === 'expired'; }).length;
    const cccmExpiringCount = cccmCerts.filter(c => getCccmExpiryStatus(c) !== 'ok').length;

    // Filter nav items based on role
    const visibleNav = isPublicView
        ? NAV_ITEMS.filter(n => ['info', 'certificates', 'cccm'].includes(n.key))
        : NAV_ITEMS;

    // ══════════════════════════ SECTIONS ══════════════════════════

    // ── SECTION 1: Thông tin cá nhân ──
    const renderInfoSection = () => (
        <div className="pf-section">
            {/* Workflow Alert */}
            {!isAdminOrHR && (
                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined style={{ color: '#2563eb' }} />}
                    style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #bfdbfe', background: '#eff6ff' }}
                    message={<span style={{ fontWeight: 600, color: '#1e40af' }}>Quy trình cập nhật hồ sơ nhân sự</span>}
                    description={
                        <span style={{ color: '#1e3a8a', fontSize: 12.5 }}>
                            Để bảo đảm tính toàn vẹn và chính xác của dữ liệu CMMS, mọi thay đổi từ tài khoản nhân viên sẽ tự động tạo một yêu cầu trình Ban Giám đốc và Phòng Nhân sự duyệt trước khi áp dụng chính thức.
                        </span>
                    }
                />
            )}

            <Form 
                form={infoForm} 
                layout="vertical" 
                disabled={!isOwnProfile && !isAdminOrHR} 
                onFinish={async (vals) => {
                    setLoading(true);
                    try {
                        const formatted = {
                            ...vals,
                            date_of_birth: vals.date_of_birth ? dayjs(vals.date_of_birth).format('YYYY-MM-DD') : null,
                            cccd_issue_date: vals.cccd_issue_date ? dayjs(vals.cccd_issue_date).format('YYYY-MM-DD') : null,
                        };
                        if (isAdminOrHR) {
                            const token = getToken();
                            await updateUser(user.user_id, formatted, token);
                            onUpdateSuccess({ ...user!, ...formatted });
                            message.success('Cập nhật thông tin thành công');
                        } else {
                            // Tất cả thông tin đều phải gửi phê duyệt (trừ ADMIN/HR)
                            const allFields: Record<string, any> = {
                                name: formatted.name,
                                phone_number: formatted.phone_number,
                                date_of_birth: formatted.date_of_birth,
                                place_of_birth: formatted.place_of_birth,
                                gender: formatted.gender,
                                ethnicity: formatted.ethnicity,
                                is_party_member: Boolean(formatted.is_party_member),
                                citizen_identification_card: formatted.citizen_identification_card,
                                cccd_issue_date: formatted.cccd_issue_date,
                                position: formatted.position,
                                permanent_address: formatted.permanent_address,
                                temporary_address: formatted.temporary_address,
                                hometown: formatted.hometown
                            };

                            // Giá trị hiện tại để so sánh
                            const oldValues: Record<string, any> = {
                                name: user.name || '',
                                phone_number: user.phone_number || '',
                                date_of_birth: user.date_of_birth ? dayjs(user.date_of_birth).format('YYYY-MM-DD') : '',
                                place_of_birth: user.place_of_birth || '',
                                gender: user.gender || '',
                                ethnicity: user.ethnicity || '',
                                is_party_member: Boolean(user.is_party_member),
                                citizen_identification_card: user.citizen_identification_card || '',
                                cccd_issue_date: user.cccd_issue_date ? dayjs(user.cccd_issue_date).format('YYYY-MM-DD') : '',
                                position: user.position || '',
                                permanent_address: user.permanent_address || '',
                                temporary_address: user.temporary_address || '',
                                hometown: user.hometown || '',
                            };

                            const changedFields: Record<string, any> = {};
                            const changedOldValues: Record<string, any> = {};

                            Object.keys(allFields).forEach((key) => {
                                const normalizedNew = allFields[key] || '';
                                const normalizedOld = oldValues[key] || '';
                                if (normalizedNew !== normalizedOld) {
                                    changedFields[key] = allFields[key];
                                    changedOldValues[key] = oldValues[key] || null;
                                }
                            });

                            if (Object.keys(changedFields).length > 0) {
                                await createProfileRequest({
                                    request_type: 'UPDATE_INFO',
                                    data_payload: {
                                        ...changedFields,
                                        _old_values: changedOldValues,
                                    }
                                });
                                message.success('Yêu cầu thay đổi đã được gửi cho Quản lý nhân sự phê duyệt.');
                            } else {
                                message.info('Không có thông tin nào thay đổi.');
                            }
                        }
                    } catch (e: any) { message.error(e.message || 'Lỗi cập nhật'); }
                    finally { setLoading(false); }
                }}
            >
                <div className="pf-bento-grid">
                    {/* ── BENTO 1: THÔNG TIN NHÂN SỰ & CHỨC DANH ── */}
                    <div className="pf-info-card">
                        <div className="pf-card-header-bar">
                            <div className="pf-card-icon-pill" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                                <SolutionOutlined />
                            </div>
                            <div>
                                <div className="pf-card-section-title">Thông tin nhân sự & Chức danh</div>
                                <div className="pf-card-section-desc">Họ tên, mã nhân viên, vị trí công tác và lý lịch cơ bản</div>
                            </div>
                        </div>
                        <div className="pf-card-body">
                            <Row gutter={[12, 4]}>
                                <Col xs={24} sm={14}>
                                    <Form.Item name="name" label={<span className="pf-field-label">Họ và tên *</span>} rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}>
                                        <Input prefix={<UserOutlined style={{ color: '#64748b' }} />} placeholder="Ví dụ: Nguyễn Văn A" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={10}>
                                    <Form.Item name="employee_code" label={<span className="pf-field-label">Mã số nhân viên</span>}>
                                        <Input 
                                            disabled={!isAdminOrHR} 
                                            prefix={<IdcardOutlined style={{ color: '#64748b' }} />} 
                                            placeholder="MSNV" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={14}>
                                    <Form.Item name="position" label={<span className="pf-field-label">Chức vụ / Chức danh</span>}>
                                        <Input prefix={<CrownOutlined style={{ color: '#64748b' }} />} placeholder="Ví dụ: Kỹ thuật viên..." />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={10}>
                                    <Form.Item name="date_of_birth" label={<span className="pf-field-label">Ngày sinh</span>}>
                                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Chọn ngày sinh" />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Form.Item name="gender" label={<span className="pf-field-label">Giới tính</span>}>
                                        <Select placeholder="-- Chọn --" allowClear>
                                            <Select.Option value="Nam">Nam</Select.Option>
                                            <Select.Option value="Nữ">Nữ</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={7}>
                                    <Form.Item name="ethnicity" label={<span className="pf-field-label">Dân tộc</span>}>
                                        <Input placeholder="Ví dụ: Kinh" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={9}>
                                    <Form.Item name="is_party_member" label={<span className="pf-field-label">Đảng viên</span>}>
                                        <Select>
                                            <Select.Option value={false}>Quần chúng</Select.Option>
                                            <Select.Option value={true}>⭐ Đảng viên</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* ── BENTO 2: LIÊN HỆ & NƠI CƯ TRÚ ── */}
                    <div className="pf-info-card">
                        <div className="pf-card-header-bar">
                            <div className="pf-card-icon-pill" style={{ background: '#fef3c7', color: '#b45309' }}>
                                <PhoneOutlined />
                            </div>
                            <div>
                                <div className="pf-card-section-title">Liên hệ & Nơi cư trú</div>
                                <div className="pf-card-section-desc">Số điện thoại liên lạc, nơi sinh và địa chỉ thường trú / tạm trú</div>
                            </div>
                        </div>
                        <div className="pf-card-body">
                            <Row gutter={[12, 4]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item 
                                        name="phone_number" 
                                        label={<span className="pf-field-label">Số điện thoại di động</span>} 
                                        rules={[{ pattern: /^[0-9]{10,11}$/, message: 'SĐT không hợp lệ (10-11 số)' }]}
                                    >
                                        <Input prefix={<PhoneOutlined style={{ color: '#64748b' }} />} placeholder="090xxxxxxx" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="place_of_birth" label={<span className="pf-field-label">Nơi sinh</span>}>
                                        <Input prefix={<EnvironmentOutlined style={{ color: '#64748b' }} />} placeholder="Ví dụ: TP. Hồ Chí Minh" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item name="permanent_address" label={<span className="pf-field-label">Địa chỉ thường trú</span>}>
                                        <Input prefix={<HomeOutlined style={{ color: '#64748b' }} />} placeholder="Địa chỉ thường trú đầy đủ theo hộ khẩu" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item name="temporary_address" label={<span className="pf-field-label">Địa chỉ nơi ở hiện tại (tạm trú)</span>}>
                                        <Input prefix={<EnvironmentOutlined style={{ color: '#64748b' }} />} placeholder="Địa chỉ nơi ở hiện tại (nếu khác nơi thường trú)" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* ── BENTO 3: ĐỊNH DANH & CĂN CƯỚC CÔNG DÂN ── */}
                    <div className="pf-info-card">
                        <div className="pf-card-header-bar">
                            <div className="pf-card-icon-pill" style={{ background: '#d1fae5', color: '#047857' }}>
                                <AuditOutlined />
                            </div>
                            <div>
                                <div className="pf-card-section-title">Định danh & Thẻ căn cước công dân</div>
                                <div className="pf-card-section-desc">Số định danh cá nhân 12 số, ngày cấp thẻ và quê quán</div>
                            </div>
                        </div>
                        <div className="pf-card-body">
                            <Row gutter={[12, 4]}>
                                <Col xs={24} sm={13}>
                                    <Form.Item 
                                        name="citizen_identification_card" 
                                        label={<span className="pf-field-label">Số CCCD (12 chữ số)</span>} 
                                        rules={[{ pattern: /^[0-9]{12}$/, message: 'CCCD phải có đúng 12 chữ số' }]}
                                    >
                                        <Input maxLength={12} prefix={<IdcardOutlined style={{ color: '#64748b' }} />} placeholder="001xxxxxxxxx" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={11}>
                                    <Form.Item name="cccd_issue_date" label={<span className="pf-field-label">Ngày cấp CCCD</span>}>
                                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Chọn ngày cấp" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item name="hometown" label={<span className="pf-field-label">Nguyên quán / Quê quán</span>}>
                                        <Input prefix={<HomeOutlined style={{ color: '#64748b' }} />} placeholder="Ví dụ: Quận 1, TP. Hồ Chí Minh" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* ── BENTO 4: CHỮ KÝ ĐIỆN TỬ MẪU ── */}
                    <div className="pf-info-card">
                        <div className="pf-card-header-bar" style={{ justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div className="pf-card-icon-pill" style={{ background: '#ede9fe', color: '#6d28d9' }}>
                                    <EditOutlined />
                                </div>
                                <div>
                                    <div className="pf-card-section-title">Chữ ký điện tử mẫu</div>
                                    <div className="pf-card-section-desc">Ký duyệt biên bản, phiếu công tác và hồ sơ nội bộ</div>
                                </div>
                            </div>
                            {(isOwnProfile || isAdminOrHR) && (
                                <Upload
                                    showUploadList={false}
                                    accept="image/*"
                                    customRequest={async (options) => {
                                        try {
                                            const token = getToken();
                                            const res = await uploadSignature(user.user_id, options.file as File, token);
                                            setSignatureUrl(res.signature_url);
                                            message.success('Tải lên chữ ký thành công');
                                            options.onSuccess?.(res);
                                            onUpdateSuccess({ ...user, signature_url: res.signature_url });
                                        } catch (err: unknown) {
                                            const errMsg = err instanceof Error ? err.message : 'Tải lên thất bại';
                                            message.error(errMsg);
                                        }
                                    }}
                                >
                                    <Button icon={<UploadOutlined />} size="small" type="primary" ghost>
                                        Tải chữ ký
                                    </Button>
                                </Upload>
                            )}
                        </div>
                        <div className="pf-card-body" style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                            {signatureUrl ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '14px 16px' }}>
                                    <div style={{ background: '#ffffff', padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                                        <img src={getBackendImageUrl(signatureUrl)} alt="Chữ ký điện tử" style={{ maxHeight: 54, maxWidth: 180, objectFit: 'contain', display: 'block' }} />
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        <div style={{ fontWeight: 600, color: '#059669', marginBottom: 2 }}>✓ Đã đăng ký chữ ký số</div>
                                        <div>Định dạng khuyến nghị: PNG nền trong suốt</div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '24px 16px', textAlign: 'center', color: '#64748b', fontSize: 12.5 }}>
                                    Chưa đăng ký chữ ký điện tử mẫu. Nhấn <b>"Tải chữ ký"</b> ở góc trên để tải ảnh lên.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {(isOwnProfile || isAdminOrHR) && (
                    <div className="pf-action-bar">
                        <div>
                            {isAdminOrHR ? (
                                <Tag color="success" icon={<CheckCircleOutlined />} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, margin: 0 }}>
                                    Chế độ Quản trị viên (Lưu trực tiếp)
                                </Tag>
                            ) : (
                                <Tag color="processing" icon={<InfoCircleOutlined />} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, margin: 0 }}>
                                    Chế độ Nhân viên (Gửi phê duyệt HR)
                                </Tag>
                            )}
                        </div>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            size="large"
                            loading={loading} 
                            icon={<SaveOutlined />}
                            className="pf-submit-btn-premium"
                        >
                            {isAdminOrHR ? 'Lưu thay đổi trực tiếp' : 'Gửi yêu cầu cập nhật hồ sơ'}
                        </Button>
                    </div>
                )}
            </Form>
        </div>
    );

    const renderCertFileLinks = (file_url?: string) => {
        if (!file_url) return null;
        let urls: string[] = [];
        try {
            const parsed = JSON.parse(file_url);
            if (Array.isArray(parsed)) urls = parsed;
            else urls = [file_url];
        } catch {
            urls = [file_url];
        }
        if (!urls.length || urls[0] === null) return null;
        
        return (
            <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}><FileTextOutlined style={{ marginRight: 4 }}/> Đính kèm: </span>
                {urls.map((u, i) => (
                    <Button key={i} size="small" type="dashed" href={getBackendImageUrl(u)} target="_blank" icon={<EyeOutlined />}>
                        File {urls.length > 1 ? i + 1 : ''}
                    </Button>
                ))}
            </div>
        );
    };

    const renderActionButtons = (cert: IEmployeeCertificate) => {
        if (!isAdminOrHR) return null;
        return (
            <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button size="small" type="text" icon={<EditOutlined style={{ color: '#1890ff' }}/>} onClick={() => handleEditQual(cert)}>Sửa</Button>
                <Popconfirm title="Xóa chứng chỉ này?" onConfirm={() => handleDeleteQual(cert.id)} okText="Xóa" cancelText="Hủy" okButtonProps={{ danger: true }}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}>Xóa</Button>
                </Popconfirm>
            </div>
        );
    };

    // ── SECTION 2: Bằng cấp & GPLX ──
    const renderCertificatesSection = () => {
        if (qualLoading) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        return (
            <div className="pf-section">
                <div className="pf-section-header">
                    <SafetyCertificateOutlined className="pf-section-icon" />
                    <span>Bằng cấp & Giấy phép lái xe</span>
                    {expiringCount > 0 && <Badge count={expiringCount} style={{ backgroundColor: '#faad14', marginLeft: 8 }} />}
                    {(isOwnProfile || isAdminOrHR) && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddQual} style={{ marginLeft: 'auto' }}>Thêm mới</Button>}
                </div>

                {/* Bằng cấp */}
                <div className="pf-subsection">
                    <div className="pf-subsection-title"><BookOutlined style={{ color: '#1890ff' }} /> Bằng cấp ({degrees.length})</div>
                    {degrees.length === 0 ? (
                        <Empty description="Chưa có bằng cấp" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
                            {degrees.map(cert => {
                                const isTHPT = cert.degree_type === 'THPT';
                                return (
                                <div key={cert.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 12, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Tag color={isTHPT ? 'gold' : 'blue'} style={{ border: 'none', background: isTHPT ? '#fffbe6' : '#e6f7ff', color: isTHPT ? '#d48806' : '#1890ff', fontWeight: 500 }}>
                                            {isTHPT ? 'Bằng TN THPT' : cert.degree_type || 'Bằng cấp'}
                                        </Tag>
                                        {cert.grading && <Tag color="green" style={{ border: 'none', background: '#f6ffed' }}>{cert.grading}</Tag>}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: '#262626' }}>
                                        {isTHPT ? (cert.school_name || '-') : (cert.major || '-')}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#595959', flex: 1 }}>
                                        {isTHPT ? (
                                            <>
                                                {cert.exam_session && <div style={{ marginBottom: 4 }}>Khoá thi: <span style={{ fontWeight: 500 }}>{dayjs(cert.exam_session).format('DD/MM/YYYY')}</span></div>}
                                                {cert.exam_council && <div style={{ marginBottom: 4 }}>Hội đồng thi: <span style={{ fontWeight: 500 }}>{cert.exam_council}</span></div>}
                                                {cert.certificate_number && <div style={{ marginBottom: 4 }}>Số hiệu: <span style={{ fontWeight: 500 }}>{cert.certificate_number}</span></div>}
                                                {cert.registry_number && <div style={{ marginBottom: 4 }}>Số vào sổ: <span style={{ fontWeight: 500 }}>{cert.registry_number}</span></div>}
                                                {cert.graduation_year && <div style={{ marginBottom: 4 }}>Năm TN: <span style={{ fontWeight: 500 }}>{cert.graduation_year}</span></div>}
                                                {cert.issue_date && <div style={{ marginBottom: 4 }}>Ngày cấp: <span style={{ fontWeight: 500 }}>{dayjs(cert.issue_date).format('DD/MM/YYYY')}</span></div>}
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ marginBottom: 4 }}>Trường: <span style={{ fontWeight: 500 }}>{cert.school_name || '-'}</span></div>
                                                {cert.graduation_year && <div style={{ marginBottom: 4 }}>Tốt nghiệp: <span style={{ fontWeight: 500 }}>{cert.graduation_year}</span></div>}
                                                {cert.study_mode && <div style={{ marginBottom: 4 }}>Hình thức: <span style={{ fontWeight: 500 }}>{cert.study_mode}</span></div>}
                                            </>
                                        )}
                                    </div>
                                    {renderCertFileLinks(cert.file_url)}
                                    {renderActionButtons(cert)}
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* GPLX */}
                <div className="pf-subsection" style={{ marginTop: 24 }}>
                    <div className="pf-subsection-title"><CarOutlined style={{ color: '#faad14' }} /> Giấy phép lái xe ({licenses.length})</div>
                    {licenses.length === 0 ? (
                        <Empty description="Chưa có GPLX" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
                            {licenses.map(cert => {
                                const status = getExpiryStatus(cert);
                                const isWarning = status === 'warning' || status === 'expired';
                                return (
                                    <div key={cert.id} style={{ border: isWarning ? '1px solid #ffccc7' : '1px solid #e8e8e8', borderRadius: 8, padding: 12, background: isWarning ? '#fff2f0' : '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                            <Tag color="orange" style={{ fontSize: 14, fontWeight: 700, padding: '2px 10px', border: 'none' }}>Hạng {cert.license_class}</Tag>
                                            {isWarning && (
                                                <Tag color={status === 'expired' ? 'error' : 'warning'} icon={<WarningOutlined />} style={{ border: 'none' }}>
                                                    {status === 'expired' ? 'Hết hạn' : `Còn ${dayjs(cert.expiry_date).diff(dayjs(), 'day')} ngày`}
                                                </Tag>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#595959', flex: 1 }}>
                                            <div style={{ marginBottom: 6 }}>Ngày cấp: <span style={{ fontWeight: 500, color: '#262626' }}>{cert.issue_date ? dayjs(cert.issue_date).format('DD/MM/YYYY') : '-'}</span></div>
                                            <div style={{ marginBottom: 6 }}>Có giá trị đến: <span style={{ fontWeight: 500, color: '#262626' }}>{cert.is_permanent ? 'Vĩnh viễn' : cert.expiry_date ? dayjs(cert.expiry_date).format('DD/MM/YYYY') : '-'}</span></div>
                                            <div style={{ marginBottom: 6 }}>Nơi cấp: <span style={{ fontWeight: 500, color: '#262626' }}>{cert.issuing_place || '-'}</span></div>
                                            <div style={{ marginBottom: 6 }}>Số GPLX: <span style={{ fontWeight: 500, color: '#262626' }}>{cert.certificate_number || '-'}</span></div>
                                        </div>
                                        {renderCertFileLinks(cert.file_url)}
                                        {renderActionButtons(cert)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Ngoại ngữ */}
                <div className="pf-subsection" style={{ marginTop: 24 }}>
                    <div className="pf-subsection-title"><GlobalOutlined style={{ color: '#eb2f96' }} /> Chứng chỉ Ngoại ngữ ({englishCerts.length})</div>
                    {englishCerts.length === 0 ? (
                        <Empty description="Chưa có thông tin" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
                            {englishCerts.map(cert => (
                                <div key={cert.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 12, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Tag color="magenta" style={{ border: 'none', background: '#fff0f6' }}>{cert.degree_type || 'Ngoại ngữ'}</Tag>
                                        {cert.grading && <Tag color="green" style={{ border: 'none' }}>{cert.grading}</Tag>}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, color: '#262626' }}>{cert.school_name || '-'}</div>
                                    <div style={{ fontSize: 13, color: '#595959', flex: 1 }}>
                                        <div style={{ marginBottom: 4 }}>Ngày cấp: <span style={{ fontWeight: 500 }}>{cert.issue_date ? dayjs(cert.issue_date).format('DD/MM/YYYY') : '-'}</span></div>
                                        <div style={{ marginBottom: 4 }}>Hết hạn: <span style={{ fontWeight: 500 }}>{cert.is_permanent ? 'Vĩnh viễn' : cert.expiry_date ? dayjs(cert.expiry_date).format('DD/MM/YYYY') : '-'}</span></div>
                                    </div>
                                    {renderCertFileLinks(cert.file_url)}
                                    {renderActionButtons(cert)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Tin học */}
                <div className="pf-subsection" style={{ marginTop: 24 }}>
                    <div className="pf-subsection-title"><DesktopOutlined style={{ color: '#13c2c2' }} /> Chứng chỉ Tin học ({itCerts.length})</div>
                    {itCerts.length === 0 ? (
                        <Empty description="Chưa có thông tin" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
                            {itCerts.map(cert => (
                                <div key={cert.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 12, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Tag color="cyan" style={{ border: 'none', background: '#e6fffb' }}>{cert.degree_type || 'Tin học'}</Tag>
                                        {cert.grading && <Tag color="green" style={{ border: 'none' }}>{cert.grading}</Tag>}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, color: '#262626' }}>{cert.school_name || '-'}</div>
                                    <div style={{ fontSize: 13, color: '#595959', flex: 1 }}>
                                        <div style={{ marginBottom: 4 }}>Ngày cấp: <span style={{ fontWeight: 500 }}>{cert.issue_date ? dayjs(cert.issue_date).format('DD/MM/YYYY') : '-'}</span></div>
                                        <div style={{ marginBottom: 4 }}>Hết hạn: <span style={{ fontWeight: 500 }}>{cert.is_permanent ? 'Vĩnh viễn' : cert.expiry_date ? dayjs(cert.expiry_date).format('DD/MM/YYYY') : '-'}</span></div>
                                    </div>
                                    {renderCertFileLinks(cert.file_url)}
                                    {renderActionButtons(cert)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ── SECTION 3: CCCM & Đào tạo (merged) ──
    const cccmColumns = [
        { title: 'STT', render: (_: any, __: any, i: number) => i + 1, width: 50 },
        { title: 'Tên chứng chỉ', dataIndex: ['program', 'name'], width: 180, ellipsis: true },
        { title: 'Ngày ban hành QĐ', dataIndex: 'issue_date', width: 120, render: (d: string) => d ? dayjs(d).format('DD/MM/YYYY') : '-' },
        {
            title: 'Hết hạn định kỳ', dataIndex: 'next_training_date', width: 120,
            render: (d: string) => {
                if (!d) return '-';
                const isExp = dayjs(d).isBefore(dayjs());
                return <span style={{ color: isExp ? '#ff4d4f' : 'inherit', fontWeight: isExp ? 700 : 400 }}>{dayjs(d).format('DD/MM/YYYY')}</span>;
            }
        },
        { title: 'Xếp loại', dataIndex: 'grading', width: 100, render: (t: string) => t || '-' },
        ...(isAdminOrHR ? [{
            title: '', key: 'action', width: 80,
            render: (_: any, record: IEmployeeCertificate) => (
                <Space size="small">
                    <Button type="text" size="small" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => handleEditCert(record)} />
                    <Popconfirm title="Xóa chứng chỉ này?" onConfirm={() => handleDeleteCert(record.id)} okText="Xóa" cancelText="Hủy" okButtonProps={{ danger: true }}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }] : []),
    ];

    const renderCccmSection = () => {
        if (cccmLoading && historyLoading) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        
        const now = dayjs();
        const splitCerts = (certs: IEmployeeCertificate[]) => {
            const active: IEmployeeCertificate[] = [];
            const history: IEmployeeCertificate[] = [];
            certs.forEach(cert => {
                if (cert.status === 'REVOKED' || cert.status === 'EXPIRED') {
                    history.push(cert);
                } else if (cert.next_training_date) {
                    const daysExpired = now.diff(dayjs(cert.next_training_date), 'day');
                    if (daysExpired >= 14) {
                        history.push(cert);
                    } else {
                        active.push(cert);
                    }
                } else {
                    active.push(cert);
                }
            });
            return { active, history };
        };

        const { active: activeReadonly, history: historyReadonly } = splitCerts(cccmCerts);
        const { active: activeEdit, history: historyEdit } = splitCerts(editCertificates);

        const readonlyColumns = [
            { title: 'Tên chứng chỉ', render: (_: any, r: IEmployeeCertificate) => (<div><div style={{ fontWeight: 600, fontSize: 13 }}>{r.program?.name || '-'}</div></div>) },
            { title: 'Ngày ban hành QĐ', width: 120, render: (_: any, r: IEmployeeCertificate) => r.issue_date ? dayjs(r.issue_date).format('DD/MM/YYYY') : '-' },
            {
                title: 'Hết hạn định kỳ', width: 120, render: (_: any, r: IEmployeeCertificate) => {
                    const end = r.end_date || r.expiry_date || r.next_training_date;
                    if (!end) return <span style={{ color: '#999' }}>Không có</span>;
                    const st = getCccmExpiryStatus ? getCccmExpiryStatus(r) : 'ok';
                    const color = st === 'expired' ? '#ff4d4f' : st === 'warning' ? '#faad14' : '#52c41a';
                    return <span style={{ color, fontWeight: 600 }}>{(st !== 'ok') && <WarningOutlined style={{ marginRight: 4 }} />}{dayjs(end).format('DD/MM/YYYY')}</span>;
                }
            },
            {
                title: 'T.Thái', width: 90, align: 'center' as const, render: (_: any, r: IEmployeeCertificate) => {
                    const st = getCccmExpiryStatus ? getCccmExpiryStatus(r) : 'ok';
                    return <Tag color={st === 'expired' ? 'red' : st === 'warning' ? 'orange' : 'green'}>{st === 'expired' ? 'Hết hạn' : st === 'warning' ? 'Sắp hết' : 'Còn HL'}</Tag>;
                }
            },
        ];

        return (
            <div className="pf-section">
                <div className="pf-section-header">
                    <BookOutlined className="pf-section-icon" />
                    <span>CCCM & Đào tạo</span>
                    {cccmExpiringCount > 0 && <Badge count={cccmExpiringCount} style={{ backgroundColor: '#faad14', marginLeft: 8 }} />}
                    {isAdminOrHR && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddCert} style={{ marginLeft: 'auto' }}>Thêm CCCM</Button>}
                </div>

                {/* CCCM Summary (read-only view) */}
                {!isAdminOrHR && (
                    <>
                        <div className="pf-subsection">
                            <div className="pf-subsection-title">Chứng chỉ chuyên môn ({activeReadonly.length})</div>
                            {activeReadonly.length === 0 ? (
                                <Empty description="Chưa có CCCM hiện tại" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <Table dataSource={activeReadonly} rowKey="id" pagination={false} size="small" bordered scroll={{ x: 'max-content' }} columns={readonlyColumns} />
                            )}
                        </div>
                        {historyReadonly.length > 0 && (
                            <div className="pf-subsection">
                                <div className="pf-subsection-title" style={{ color: '#8c8c8c' }}>Lịch sử đào tạo ({historyReadonly.length})</div>
                                <Table dataSource={historyReadonly} rowKey="id" pagination={{ pageSize: 5 }} size="small" bordered scroll={{ x: 'max-content' }} columns={readonlyColumns} />
                            </div>
                        )}
                    </>
                )}

                {/* CCCM Editable Table (Admin/HR view) */}
                {isAdminOrHR && (
                    <>
                        <div className="pf-subsection">
                            <div className="pf-subsection-title">Quản lý CCCM ({activeEdit.length})</div>
                            {activeEdit.length === 0 ? (
                                <Empty description="Chưa có CCCM hiện tại" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <Table dataSource={activeEdit} columns={cccmColumns} rowKey="id" loading={historyLoading} scroll={{ x: 'max-content' }} pagination={false} bordered size="small" />
                            )}
                        </div>
                        {historyEdit.length > 0 && (
                            <div className="pf-subsection">
                                <div className="pf-subsection-title" style={{ color: '#8c8c8c' }}>Lịch sử đào tạo ({historyEdit.length})</div>
                                <Table dataSource={historyEdit} columns={cccmColumns} rowKey="id" loading={historyLoading} scroll={{ x: 'max-content' }} pagination={{ pageSize: 5 }} bordered size="small" />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // ── SECTION 4: Hợp đồng ──
    const renderContractsSection = () => {
        if (historyLoading && !loadedTabs.contracts) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        return (
            <div className="pf-section">
                <div className="pf-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <FileTextOutlined className="pf-section-icon" />
                        <span>Hợp đồng lao động</span>
                    </div>
                    {isAdminOrHR && (
                        <Button 
                            type="primary" 
                            size="small" 
                            icon={<PlusOutlined />}
                            onClick={() => {
                                contractForm.resetFields();
                                setAddContractOpen(true);
                            }}
                        >
                            Thêm hợp đồng
                        </Button>
                    )}
                </div>
                {laborContracts.length === 0 ? (
                    <Empty description="Chưa có thông tin hợp đồng" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div className="pf-card-list">
                        {laborContracts.map(c => (
                            <div key={c.id} className="pf-card">
                                <div className="pf-card-top">
                                    <span style={{ fontWeight: 600, color: '#1890ff' }}>{c.contract_number}</span>
                                    {(() => {
                                        if (c.status === 'TERMINATED') return <Tag color="red">Chấm dứt</Tag>;
                                        if (c.status === 'EXPIRED') return <Tag color="orange">Đã hết hạn</Tag>;
                                        if (c.end_date) {
                                            const end = dayjs(c.end_date);
                                            const now = dayjs();
                                            if (end.isBefore(now)) return <Tag color="red">Hết hạn (Chưa cập nhật)</Tag>;
                                            const daysLeft = end.diff(now, 'day');
                                            if (daysLeft <= 30) return <Tag color="warning">Sắp hết hạn ({daysLeft} ngày)</Tag>;
                                        }
                                        return <Tag color="green">Còn hiệu lực</Tag>;
                                    })()}
                                </div>
                                <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                                    <Descriptions.Item label="Chức danh">{c.job_title || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Loại HĐ">
                                        {{
                                            '1_MONTH': '1 Tháng',
                                            'APPRENTICESHIP': 'Học việc',
                                            'PROBATION': 'Thử việc',
                                            '12_MONTHS': '12 tháng',
                                            '24_MONTHS': '2 Năm',
                                            '36_MONTHS': '36 tháng',
                                            'INDEFINITE': 'Vô thời hạn'
                                        }[c.contract_type as string] || c.contract_type}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Thời hạn">
                                        {dayjs(c.start_date).format('DD/MM/YYYY')} → {c.end_date ? dayjs(c.end_date).format('DD/MM/YYYY') : 'Vô thời hạn'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // ── SECTION 5: Khen thưởng / Kỷ luật ──
    const renderHistorySection = () => {
        if (historyLoading && !loadedTabs.history) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        return (
            <div className="pf-section">
                <div className="pf-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <TrophyOutlined className="pf-section-icon" />
                        <span>Khen thưởng & Kỷ luật</span>
                    </div>
                    {isAdminOrHR && (
                        <Button 
                            type="primary" 
                            size="small" 
                            icon={<PlusOutlined />}
                            style={{ background: '#722ed1', borderColor: '#722ed1' }}
                            onClick={() => {
                                rewardForm.resetFields();
                                setAddRewardOpen(true);
                            }}
                        >
                            Thêm KT/KL
                        </Button>
                    )}
                </div>
                {rewardDisciplines.length === 0 ? (
                    <Empty description="Chưa có thông tin khen thưởng / kỷ luật" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div className="pf-card-list">
                        {rewardDisciplines.map(rec => (
                            <div key={rec.id} className={`pf-card pf-card--${rec.record_type === 'REWARD' ? 'reward' : 'discipline'}`}>
                                <div className="pf-card-top">
                                    <span style={{ fontWeight: 600 }}>{rec.record_type === 'REWARD' ? '🏆 Thưởng' : '⚠️ Kỷ luật'}: {rec.decision_number}</span>
                                    <span className="pf-card-date">Hiệu lực: {dayjs(rec.effective_date).format('DD/MM/YYYY')}</span>
                                </div>
                                <div className="pf-card-sub" style={{ marginTop: 6 }}>{rec.content}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // ── SECTION 6: Phép năm & Nghỉ bù ──
    const renderLeavesSection = () => {
        if (historyLoading && !loadedTabs.leaves) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        const currentYearLeaves = annualLeaves.filter(l => l.year === dayjs().year());
        return (
            <div className="pf-section">
                <div className="pf-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarOutlined className="pf-section-icon" />
                        <span>Quỹ Phép năm & Nghỉ bù {dayjs().year()}</span>
                    </div>
                    <Button 
                        type="primary" 
                        size="small" 
                        icon={<PlusOutlined />}
                        onClick={handleOpenRecordLeave}
                    >
                        Ghi nhận phép / nghỉ bù
                    </Button>
                </div>
                {currentYearLeaves.length === 0 ? (
                    <Empty description="Chưa có thông tin phép năm" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div className="pf-card-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                        {currentYearLeaves.map(rec => {
                            const total = (Number(rec.leave_balance_n2) || 0) + (Number(rec.leave_balance_n1) || 0) + (Number(rec.current_year_leave) || 0);
                            const taken = (Number(rec.m1_taken) || 0) + (Number(rec.m2_taken) || 0) + (Number(rec.m3_taken) || 0) + (Number(rec.m4_taken) || 0) + 
                                (Number(rec.m5_taken) || 0) + (Number(rec.m6_taken) || 0) + (Number(rec.m7_taken) || 0) + (Number(rec.m8_taken) || 0) + 
                                (Number(rec.m9_taken) || 0) + (Number(rec.m10_taken) || 0) + (Number(rec.m11_taken) || 0) + (Number(rec.m12_taken) || 0);
                            const remain = total - taken;
                            const compQuota = Number(rec.compensatory_leave_quota) || 0;
                            const compTaken = Number(rec.compensatory_leave_taken) || 0;
                            const compRemain = compQuota - compTaken;

                            return (
                                <React.Fragment key={rec.id}>
                                    {/* Quỹ Phép Năm */}
                                    <div className="pf-card">
                                        <div className="pf-card-top">
                                            <span style={{ fontWeight: 600, color: '#1890ff' }}>🏖️ Quỹ Phép Năm {rec.year}</span>
                                            <Tag color={remain < 0 ? 'red' : remain === 0 ? 'orange' : 'green'}>Còn lại: {remain} ngày</Tag>
                                        </div>
                                        <div className="pf-leave-stats">
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Tổng quỹ phép</span>
                                                <span className="pf-leave-stat-value" style={{ color: '#1890ff' }}>{total}</span>
                                            </div>
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Đã sử dụng</span>
                                                <span className="pf-leave-stat-value" style={{ color: '#ff4d4f' }}>{taken}</span>
                                            </div>
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Còn lại</span>
                                                <span className="pf-leave-stat-value" style={{ color: remain < 0 ? '#ff4d4f' : '#52c41a' }}>{remain}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quỹ Nghỉ Bù */}
                                    <div className="pf-card" style={{ borderColor: '#d3adf7' }}>
                                        <div className="pf-card-top">
                                            <span style={{ fontWeight: 600, color: '#722ed1' }}>⏰ Quỹ Nghỉ Bù Tích Luỹ {rec.year}</span>
                                            <Tag color={compRemain < 0 ? 'red' : compRemain === 0 ? 'default' : 'purple'}>Còn tồn: {compRemain} ngày</Tag>
                                        </div>
                                        <div className="pf-leave-stats">
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Tích luỹ cấp bù</span>
                                                <span className="pf-leave-stat-value" style={{ color: '#722ed1' }}>{compQuota}</span>
                                            </div>
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Đã nghỉ bù</span>
                                                <span className="pf-leave-stat-value" style={{ color: '#fa8c16' }}>{compTaken}</span>
                                            </div>
                                            <div className="pf-leave-stat">
                                                <span className="pf-leave-stat-label">Còn tồn bù</span>
                                                <span className="pf-leave-stat-value" style={{ color: compRemain < 0 ? '#ff4d4f' : '#722ed1' }}>{compRemain}</span>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                <div className="pf-subsection" style={{ marginTop: 18 }}>
                    <div className="pf-subsection-title">Lịch sử nghỉ phép & Giấy đề nghị ({leaveHistories.length})</div>
                    {leaveHistories.length === 0 ? (
                        <Empty description="Chưa có lịch sử nghỉ phép" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <Table 
                            dataSource={leaveHistories} 
                            rowKey="id" 
                            pagination={{ pageSize: 5 }} 
                            size="small" 
                            bordered 
                            scroll={{ x: 750 }}
                            columns={[
                                {
                                    title: 'Thời gian nghỉ',
                                    width: 170,
                                    render: (_: any, r: any) => (
                                        <span>{dayjs(r.from_date).format('DD/MM/YYYY')} - {dayjs(r.to_date).format('DD/MM/YYYY')}</span>
                                    )
                                },
                                {
                                    title: 'Loại nghỉ',
                                    width: 135,
                                    dataIndex: 'leave_type',
                                    render: (type: LeaveType) => {
                                        const cfg = LEAVE_TYPE_CONFIG[type] || LEAVE_TYPE_CONFIG.ANNUAL;
                                        return <Tag color={cfg.color}>{cfg.label}</Tag>;
                                    }
                                },
                                {
                                    title: 'Số ngày',
                                    width: 80,
                                    dataIndex: 'days_taken',
                                    align: 'center' as const,
                                    render: (val: number) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{val}</span>
                                },
                                {
                                    title: 'Lý do nghỉ',
                                    dataIndex: 'reason',
                                    render: (reason: string, r: any) => (
                                        <div>
                                            <div>{reason || '-'}</div>
                                            {r.location && <div style={{ fontSize: 11, color: '#8c8c8c' }}>📍 {r.location}</div>}
                                        </div>
                                    )
                                },
                                {
                                    title: 'Giấy đề nghị',
                                    width: 150,
                                    dataIndex: 'attachments',
                                    render: (attachments: string[]) => {
                                        if (!attachments || attachments.length === 0) return <span style={{ color: '#999' }}>Không có</span>;
                                        return (
                                            <Space direction="vertical" size="small">
                                                {attachments.map((url, i) => {
                                                    const isPdf = url.toLowerCase().endsWith('.pdf');
                                                    return (
                                                        <a 
                                                            key={i} 
                                                            href={`${process.env.REACT_APP_BASE_URL?.replace('/api', '')}${url}`} 
                                                            target="_blank" 
                                                            rel="noreferrer" 
                                                            style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, color: isPdf ? '#ff4d4f' : '#1890ff', fontWeight: 500 }}
                                                        >
                                                            {isPdf ? <FilePdfOutlined style={{ color: '#ff4d4f' }} /> : <FileTextOutlined style={{ color: '#1890ff' }} />} 
                                                            {attachments.length > 1 ? `Giấy đề nghị (${i + 1})` : 'Giấy đề nghị'}
                                                        </a>
                                                    );
                                                })}
                                            </Space>
                                        );
                                    }
                                },
                                {
                                    title: 'Ngày ghi nhận',
                                    width: 110,
                                    dataIndex: 'created_at',
                                    render: (d: string) => <span style={{ color: '#8c8c8c' }}>{dayjs(d).format('DD/MM/YYYY')}</span>
                                }
                            ]} 
                        />
                    )}
                </div>
            </div>
        );
    };

    // ── SECTION 7: My Requests ──
    const renderMyRequestsSection = () => {
        if (historyLoading && !loadedTabs.my_requests) return <div className="pf-loading"><Spin tip="Đang tải..." /></div>;
        return (
            <div className="pf-section">
                <div className="pf-section-header">
                    <IdcardOutlined className="pf-section-icon" />
                    <span>Lịch sử yêu cầu thay đổi</span>
                </div>
                {myRequests.length === 0 ? (
                    <Empty description="Bạn chưa có yêu cầu nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div className="pf-card-list">
                        {myRequests.map(req => (
                            <div key={req.id} className={`pf-card pf-card--${req.status === 'PENDING' ? 'warning' : req.status === 'REJECTED' ? 'expired' : 'reward'}`}>
                                <div className="pf-card-top">
                                    <span style={{ fontWeight: 600 }}>{req.request_type === 'UPDATE_INFO' ? 'Cập nhật TT' : 'Tải lên Bằng cấp'}</span>
                                    <Tag color={req.status === 'PENDING' ? 'orange' : req.status === 'APPROVED' ? 'green' : 'red'}>
                                        {req.status === 'PENDING' ? '⏳ Chờ duyệt' : req.status === 'APPROVED' ? '✅ Đã duyệt' : '❌ Từ chối'}
                                    </Tag>
                                </div>
                                <div className="pf-card-date" style={{ marginTop: 6 }}>Gửi ngày: {dayjs(req.created_at).format('DD/MM/YYYY HH:mm')}</div>
                                {req.status === 'REJECTED' && req.notes && (
                                    <div style={{ marginTop: 8, padding: 8, background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#cf1322' }}>Lý do từ chối:</div>
                                        <div style={{ fontSize: 13, color: '#cf1322' }}>{req.notes}</div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // ── SECTION 8: Tasks & KPI Summary (360° Integration) ──
    const renderTasksKpiSection = () => {
        if (historyLoading && !loadedTabs.tasks_kpi) {
            return <div className="pf-loading"><Spin tip="Đang tải dữ liệu hiệu suất công việc..." /></div>;
        }
        if (!taskSummary) {
            return (
                <div className="pf-section">
                    <Empty description="Chưa có dữ liệu công việc & KPI" />
                </div>
            );
        }

        const gradeColorMap: Record<string, { bg: string; color: string; border: string }> = {
            'A+': { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
            'A': { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
            'B': { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
            'C': { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
        };
        const gStyle = gradeColorMap[taskSummary.grade] || gradeColorMap.B;

        return (
            <div className="pf-section">
                <div
                    style={{
                        background: '#ffffff',
                        borderRadius: 14,
                        padding: '16px 20px',
                        border: '1px solid #dbe3ee',
                        marginBottom: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 10,
                                background: '#eff6ff',
                                color: '#2563eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 20,
                            }}
                        >
                            <TrophyOutlined />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                                Tổng hợp Hiệu suất Công việc & Điểm KPI Thi đua
                            </div>
                            <div style={{ fontSize: 12.5, color: '#64748b' }}>
                                Tự động liên thông từ Phân hệ Quản lý Công việc & Giao việc nội bộ
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            padding: '6px 16px',
                            borderRadius: 999,
                            background: gStyle.bg,
                            color: gStyle.color,
                            border: `1px solid ${gStyle.border}`,
                            fontWeight: 800,
                            fontSize: 13.5,
                        }}
                    >
                        Xếp loại hiện tại: Loại {taskSummary.grade} — {taskSummary.gradeLabel}
                    </div>
                </div>

                <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={6}>
                        <div style={{ background: '#ffffff', borderRadius: 12, padding: 14, border: '1px solid #dbe3ee', textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Tổng việc được giao</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: '#2563eb', marginTop: 4 }}>{taskSummary.totalAssigned}</div>
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <div style={{ background: '#ffffff', borderRadius: 12, padding: 14, border: '1px solid #dbe3ee', textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Đã hoàn thành (Đúng hạn)</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a', marginTop: 4 }}>
                                {taskSummary.completedTotal} <span style={{ fontSize: 13, fontWeight: 600 }}>({taskSummary.onTimeRate}%)</span>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <div style={{ background: '#ffffff', borderRadius: 12, padding: 14, border: '1px solid #dbe3ee', textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Đang làm / Quá hạn</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: taskSummary.overdueUnfinished > 0 ? '#dc2626' : '#0284c7', marginTop: 4 }}>
                                {taskSummary.inProgress} / <span style={{ color: '#dc2626' }}>{taskSummary.overdueUnfinished}</span>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <div style={{ background: '#ffffff', borderRadius: 12, padding: 14, border: '1px solid #dbe3ee', textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Tổng điểm KPI tích lũy</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: '#d97706', marginTop: 4 }}>
                                {taskSummary.netKpiScore} <span style={{ fontSize: 12, fontWeight: 600 }}>điểm</span>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div style={{ background: '#ffffff', borderRadius: 14, padding: 18, border: '1px solid #dbe3ee' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12 }}>
                        Danh sách công việc gần đây ({taskSummary.recentTasks.length})
                    </div>
                    {taskSummary.recentTasks.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa được giao công việc nào" />
                    ) : (
                        <Table
                            rowKey="id"
                            size="small"
                            pagination={{ pageSize: 5 }}
                            dataSource={taskSummary.recentTasks}
                            columns={[
                                {
                                    title: 'Mã CV',
                                    dataIndex: 'code',
                                    key: 'code',
                                    width: 115,
                                    render: (c: string) => <Tag color="blue" style={{ fontWeight: 700 }}>{c}</Tag>,
                                },
                                {
                                    title: 'Nội dung công việc',
                                    dataIndex: 'title',
                                    key: 'title',
                                    render: (t: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{t}</span>,
                                },
                                {
                                    title: 'Người giao',
                                    key: 'assigner',
                                    width: 150,
                                    render: (_: unknown, r) => r.assigner?.name || '—',
                                },
                                {
                                    title: 'Hạn hoàn thành',
                                    dataIndex: 'deadline',
                                    key: 'deadline',
                                    width: 135,
                                    render: (d: string) => dayjs(d).format('DD/MM/YYYY'),
                                },
                                {
                                    title: 'Trạng thái',
                                    dataIndex: 'status',
                                    key: 'status',
                                    width: 135,
                                    render: (st: string) => {
                                        const map: Record<string, { label: string; color: string }> = {
                                            NEW: { label: 'Mới giao', color: 'blue' },
                                            CHANGE_REQUESTED: { label: 'Xin điều chỉnh', color: 'orange' },
                                            IN_PROGRESS: { label: 'Đang thực hiện', color: 'processing' },
                                            PENDING_REVIEW: { label: 'Chờ nghiệm thu', color: 'purple' },
                                            COMPLETED: { label: 'Đã hoàn thành', color: 'success' },
                                            REJECTED: { label: 'Yêu cầu làm lại', color: 'error' },
                                        };
                                        const cfg = map[st] || { label: st, color: 'default' };
                                        return <Tag color={cfg.color}>{cfg.label}</Tag>;
                                    },
                                },
                                {
                                    title: 'Điểm KPI',
                                    dataIndex: 'kpi_score',
                                    key: 'kpi_score',
                                    width: 100,
                                    align: 'center',
                                    render: (sc?: number | null) =>
                                        sc !== null && sc !== undefined ? (
                                            <Tag color="gold" style={{ fontWeight: 700 }}>+{sc} đ</Tag>
                                        ) : (
                                            <span style={{ color: '#94a3b8' }}>—</span>
                                        ),
                                },
                            ]}
                        />
                    )}
                </div>
            </div>
        );
    };

    // ── Section Router ──
    const renderActiveSection = () => {
        switch (activeSection) {
            case 'info': return renderInfoSection();
            case 'certificates': return renderCertificatesSection();
            case 'cccm': return renderCccmSection();
            case 'contracts': return renderContractsSection();
            case 'history': return renderHistorySection();
            case 'leaves': return renderLeavesSection();
            case 'tasks_kpi': return renderTasksKpiSection();
            case 'my_requests': return renderMyRequestsSection();
            default: return null;
        }
    };

    // ══════════════════════════ RENDER ══════════════════════════
    return (
        <>
            <Modal
                centered
                closable={false}
                title={null}
                footer={null}
                open={open}
                onCancel={onCancel}
                width="min(1160px, 96vw)"
                wrapClassName="pf-modal-wrap"
                className="pf-centered-modal"
                styles={{
                    container: {
                        padding: 0,
                        borderRadius: 16,
                        overflow: 'hidden',
                        background: '#e8eef5',
                        boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)',
                    },
                    body: { padding: 0 },
                }}
                destroyOnClose
            >
                <div className="pf-layout">
                    {/* ── Top Profile Banner (Full-bleed Horizontal Header) ── */}
                    <header className="pf-top-header">
                        <div className="pf-top-header-main">
                            {/* Avatar */}
                            <div className="pf-top-avatar-col">
                                {(isOwnProfile || isAdminOrHR) ? (
                                    <Upload
                                        showUploadList={false}
                                        accept="image/*"
                                        customRequest={async (options) => {
                                            try {
                                                const token = getToken();
                                                const optimizedFile = await optimizeImageBeforeUpload(options.file as File, 400, 0.8);
                                                const res = await uploadAvatar(user.user_id, optimizedFile, token);
                                                setAvatarUrl(res.avatar);
                                                if (isOwnProfile && setAuthUser) {
                                                    setAuthUser((prev) => prev ? { ...prev, avatar: res.avatar } : prev);
                                                }
                                                message.success('Cập nhật ảnh đại diện thành công');
                                                options.onSuccess?.(res);
                                                onUpdateSuccess({ ...user, avatar: res.avatar });
                                            } catch (err: unknown) {
                                                const errMsg = err instanceof Error ? err.message : 'Tải lên thất bại';
                                                message.error(errMsg);
                                            }
                                        }}
                                    >
                                        <div className="pf-avatar-wrapper">
                                            <Avatar size={64} src={avatarUrl ? getBackendImageUrl(avatarUrl) : undefined} icon={<UserOutlined />} className="pf-avatar" />
                                            <div className="pf-avatar-edit-badge">
                                                <EditOutlined style={{ color: '#fff', fontSize: 11 }} />
                                            </div>
                                        </div>
                                    </Upload>
                                ) : (
                                    <div className="pf-avatar-wrapper">
                                        <Avatar size={64} src={avatarUrl ? getBackendImageUrl(avatarUrl) : undefined} icon={<UserOutlined />} className="pf-avatar" />
                                    </div>
                                )}
                            </div>

                            {/* User Identity + Quick Chips */}
                            <div className="pf-top-identity">
                                <div className="pf-top-title-row">
                                    <span className="pf-user-name">{user.name}</span>
                                    <span className="pf-role-badge">
                                        <span className="pf-role-dot"></span>
                                        {user.position || getRoleLabel(user.role)}
                                    </span>
                                    {user.is_party_member && (
                                        <span className="pf-party-badge">⭐ Đảng viên</span>
                                    )}
                                    {user.user_departments && user.user_departments.length > 0 && (
                                        <div className="pf-top-depts">
                                            {[...user.user_departments]
                                                .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                                                .map((ud, idx) => {
                                                    const roleLabel = ud.role_in_dept === 'LEAD' ? 'Trưởng' : ud.role_in_dept === 'DEPUTY_LEAD' ? 'Phó' : '';
                                                    const deptName = ud.department?.name || `Dept#${ud.dept_id}`;
                                                    return (
                                                        <span key={idx} className={`pf-dept-pill ${ud.is_primary ? 'pf-dept-pill--primary' : ''}`}>
                                                            {ud.is_primary && <span style={{ color: '#fbbf24' }}>★</span>}
                                                            <span>{deptName}</span>
                                                            {roleLabel && <span style={{ opacity: 0.8 }}>({roleLabel})</span>}
                                                        </span>
                                                    );
                                                })}
                                        </div>
                                    )}
                                </div>

                                <div className="pf-top-meta-row">
                                    <Tooltip title="Nhấp để sao chép MSNV">
                                        <div
                                            className="pf-meta-chip"
                                            onClick={() => {
                                                if (user.employee_code) {
                                                    navigator.clipboard.writeText(user.employee_code);
                                                    message.success(`Đã sao chép MSNV: ${user.employee_code}`);
                                                }
                                            }}
                                        >
                                            <IdcardOutlined className="pf-meta-chip-icon" />
                                            <span className="pf-meta-chip-label">MSNV:</span>
                                            <span className="pf-meta-chip-val">{user.employee_code || '-'}</span>
                                            {user.employee_code && <CopyOutlined className="pf-meta-chip-copy" />}
                                        </div>
                                    </Tooltip>

                                    <Tooltip title="Nhấp để sao chép Email">
                                        <div
                                            className="pf-meta-chip"
                                            onClick={() => {
                                                if (user.email) {
                                                    navigator.clipboard.writeText(user.email);
                                                    message.success(`Đã sao chép Email: ${user.email}`);
                                                }
                                            }}
                                        >
                                            <MailOutlined className="pf-meta-chip-icon" />
                                            <span className="pf-meta-chip-label">Email:</span>
                                            <span className="pf-meta-chip-val">{user.email || '-'}</span>
                                            {user.email && <CopyOutlined className="pf-meta-chip-copy" />}
                                        </div>
                                    </Tooltip>

                                    <Tooltip title="Nhấp để sao chép Số điện thoại">
                                        <div
                                            className="pf-meta-chip"
                                            onClick={() => {
                                                if (user.phone_number) {
                                                    navigator.clipboard.writeText(user.phone_number);
                                                    message.success(`Đã sao chép SĐT: ${user.phone_number}`);
                                                }
                                            }}
                                        >
                                            <PhoneOutlined className="pf-meta-chip-icon" />
                                            <span className="pf-meta-chip-label">SĐT:</span>
                                            <span className="pf-meta-chip-val">{user.phone_number || '-'}</span>
                                            {user.phone_number && <CopyOutlined className="pf-meta-chip-copy" />}
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>

                            {/* Right Header Actions: Signature + Password + Close */}
                            <div className="pf-top-actions">
                                <div className="pf-sig-pill">
                                    <span
                                        className="pf-sig-pill-status"
                                        onClick={() => setActiveSection('info')}
                                        title="Xem chữ ký điện tử tại tab Thông tin cá nhân"
                                    >
                                        <EditOutlined style={{ color: '#60a5fa' }} />
                                        <span>Chữ ký số:</span>
                                        {signatureUrl ? (
                                            <strong style={{ color: '#34d399' }}>✓ Đã có</strong>
                                        ) : (
                                            <strong style={{ color: '#94a3b8' }}>Chưa có</strong>
                                        )}
                                    </span>
                                    {(isOwnProfile || isAdminOrHR) && (
                                        <Upload
                                            showUploadList={false}
                                            accept="image/*"
                                            customRequest={async (options) => {
                                                try {
                                                    const token = getToken();
                                                    const res = await uploadSignature(user.user_id, options.file as File, token);
                                                    setSignatureUrl(res.signature_url);
                                                    message.success('Tải lên chữ ký thành công');
                                                    options.onSuccess?.(res);
                                                    onUpdateSuccess({ ...user, signature_url: res.signature_url });
                                                } catch (err: unknown) {
                                                    const errMsg = err instanceof Error ? err.message : 'Tải lên thất bại';
                                                    message.error(errMsg);
                                                }
                                            }}
                                        >
                                            <button type="button" className="pf-sig-pill-btn">
                                                <UploadOutlined /> Cập nhật
                                            </button>
                                        </Upload>
                                    )}
                                </div>

                                {(isOwnProfile || currentUser?.role === 'ADMIN') && (
                                    <button
                                        type="button"
                                        onClick={() => setPasswordModalOpen(true)}
                                        className="pf-top-pass-btn"
                                    >
                                        <LockOutlined />
                                        <span>Đổi mật khẩu</span>
                                    </button>
                                )}

                                <Tooltip title="Đóng cửa sổ (ESC)">
                                    <button type="button" onClick={onCancel} className="pf-top-close-btn" aria-label="Đóng">
                                        <CloseOutlined />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>

                        {/* ── Horizontal Navigation Tabs Bar ── */}
                        <nav className="pf-top-tabs">
                            {visibleNav.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`pf-top-tab-item ${activeSection === item.key ? 'pf-top-tab-item--active' : ''}`}
                                    onClick={() => setActiveSection(item.key)}
                                >
                                    <span className="pf-top-tab-icon">{item.icon}</span>
                                    <span>{item.label}</span>
                                    {item.key === 'certificates' && expiringCount > 0 && (
                                        <Badge count={expiringCount} size="small" style={{ backgroundColor: '#f59e0b', boxShadow: 'none' }} />
                                    )}
                                    {item.key === 'cccm' && cccmExpiringCount > 0 && (
                                        <Badge count={cccmExpiringCount} size="small" style={{ backgroundColor: '#ef4444', boxShadow: 'none' }} />
                                    )}
                                </button>
                            ))}
                        </nav>
                    </header>

                    {/* ── Main Content Workspace ── */}
                    <main className="pf-content">
                        {renderActiveSection()}
                    </main>
                </div>

                {/* Modals */}
                <QualificationModal
                    open={qualModalOpen}
                    onCancel={() => setQualModalOpen(false)}
                    onOk={handleQualModalOk}
                    initialValues={selectedQual}
                    loading={qualModalLoading}
                    user={user}
                    okText={isAdminOrHR ? undefined : "Gửi yêu cầu phê duyệt"}
                />
                <CertificateModal
                    open={isCertModalOpen}
                    onCancel={() => setIsCertModalOpen(false)}
                    onOk={handleCertModalOk}
                    initialValues={selectedCert}
                    loading={modalLoading}
                    trainingPrograms={trainingPrograms}
                    certificateType="CCCM"
                    user={user}
                />

                {/* ── Styles ── */}
                <style>{`
                    /* Full-bleed zero-padding modal override for Ant Design (.ant-modal-container & .ant-modal-content) */
                    .pf-modal-wrap .ant-modal,
                    .pf-centered-modal {
                        padding-bottom: 0 !important;
                    }
                    .pf-modal-wrap .ant-modal-container,
                    .pf-centered-modal .ant-modal-container,
                    .pf-modal-wrap .ant-modal-content,
                    .pf-centered-modal .ant-modal-content {
                        padding: 0 !important;
                        border-radius: 16px !important;
                        overflow: hidden !important;
                        background: #e8eef5 !important;
                        box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.35) !important;
                    }
                    .pf-modal-wrap .ant-modal-body,
                    .pf-centered-modal .ant-modal-body {
                        padding: 0 !important;
                    }

                    .pf-layout {
                        display: flex;
                        flex-direction: column;
                        height: min(86vh, 800px);
                        min-height: 560px;
                        background: #e8eef5;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    }

                    /* ──── Top Profile Header Banner (With Integrated Glassmorphism Tabs) ──── */
                    .pf-top-header {
                        background: linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #172554 100%);
                        color: #fff;
                        flex-shrink: 0;
                        border-bottom: 1px solid rgba(15, 23, 42, 0.12);
                    }
                    .pf-top-header-main {
                        display: flex;
                        align-items: center;
                        gap: 18px;
                        padding: 16px 24px 14px;
                    }
                    .pf-top-avatar-col {
                        flex-shrink: 0;
                    }
                    .pf-avatar-wrapper {
                        position: relative;
                        display: inline-block;
                        cursor: pointer;
                    }
                    .pf-avatar {
                        border: 2.5px solid rgba(255, 255, 255, 0.9) !important;
                        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35), 0 6px 16px rgba(0, 0, 0, 0.3);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                    }
                    .pf-avatar-wrapper:hover .pf-avatar {
                        transform: scale(1.04);
                        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.65), 0 8px 20px rgba(0, 0, 0, 0.4);
                    }
                    .pf-avatar-edit-badge {
                        position: absolute;
                        bottom: 0;
                        right: 0;
                        width: 22px;
                        height: 22px;
                        background: #2563eb;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 2px solid #0f172a;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                    }

                    .pf-top-identity {
                        flex: 1;
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    .pf-top-title-row {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .pf-user-name {
                        font-size: 18px;
                        font-weight: 700;
                        color: #f8fafc;
                        letter-spacing: -0.2px;
                        line-height: 1.25;
                        margin-right: 2px;
                    }
                    .pf-role-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 5px;
                        padding: 2px 10px;
                        border-radius: 20px;
                        font-size: 11.5px;
                        font-weight: 600;
                        background: rgba(59, 130, 246, 0.18);
                        border: 1px solid rgba(96, 165, 250, 0.4);
                        color: #bfdbfe;
                    }
                    .pf-role-dot {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: #38bdf8;
                    }
                    .pf-party-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        padding: 2px 10px;
                        border-radius: 20px;
                        font-size: 11.5px;
                        font-weight: 600;
                        background: rgba(239, 68, 68, 0.2);
                        border: 1px solid rgba(248, 113, 113, 0.45);
                        color: #fecaca;
                    }
                    .pf-top-depts {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        flex-wrap: wrap;
                    }
                    .pf-dept-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        padding: 2px 9px;
                        border-radius: 6px;
                        font-size: 11.5px;
                        background: rgba(255, 255, 255, 0.08);
                        border: 1px solid rgba(255, 255, 255, 0.12);
                        color: #e2e8f0;
                    }
                    .pf-dept-pill--primary {
                        background: rgba(251, 191, 36, 0.14);
                        border-color: rgba(251, 191, 36, 0.35);
                        color: #fde68a;
                        font-weight: 600;
                    }

                    /* Horizontal Meta Chips */
                    .pf-top-meta-row {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .pf-meta-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 4px 10px;
                        background: rgba(255, 255, 255, 0.06);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 7px;
                        font-size: 12px;
                        color: #cbd5e1;
                        cursor: pointer;
                        transition: all 0.18s ease;
                    }
                    .pf-meta-chip:hover {
                        background: rgba(255, 255, 255, 0.12);
                        border-color: rgba(255, 255, 255, 0.22);
                        color: #ffffff;
                    }
                    .pf-meta-chip-icon {
                        color: #60a5fa;
                        font-size: 12px;
                    }
                    .pf-meta-chip-label {
                        color: #94a3b8;
                        font-size: 11.5px;
                    }
                    .pf-meta-chip-val {
                        color: #f8fafc;
                        font-weight: 600;
                    }
                    .pf-meta-chip-copy {
                        font-size: 11px;
                        color: rgba(255, 255, 255, 0.35);
                        margin-left: 2px;
                    }
                    .pf-meta-chip:hover .pf-meta-chip-copy {
                        color: #60a5fa;
                    }

                    /* Right Header Actions */
                    .pf-top-actions {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        flex-shrink: 0;
                        align-self: flex-start;
                    }
                    .pf-sig-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        background: rgba(255, 255, 255, 0.06);
                        border: 1px solid rgba(255, 255, 255, 0.12);
                        border-radius: 8px;
                        padding: 5px 10px;
                        font-size: 12px;
                    }
                    .pf-sig-pill-status {
                        display: inline-flex;
                        align-items: center;
                        gap: 5px;
                        color: #cbd5e1;
                        cursor: pointer;
                    }
                    .pf-sig-pill-btn {
                        border: none;
                        background: rgba(59, 130, 246, 0.22);
                        color: #93c5fd;
                        border-radius: 5px;
                        padding: 2px 8px;
                        font-size: 11.5px;
                        font-weight: 600;
                        cursor: pointer;
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        transition: all 0.18s;
                    }
                    .pf-sig-pill-btn:hover {
                        background: #2563eb;
                        color: #ffffff;
                    }
                    .pf-top-pass-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        height: 32px;
                        padding: 0 12px;
                        border-radius: 8px;
                        border: 1px solid rgba(248, 113, 113, 0.35);
                        background: rgba(239, 68, 68, 0.14);
                        color: #fca5a5;
                        font-size: 12px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.18s;
                    }
                    .pf-top-pass-btn:hover {
                        background: #ef4444;
                        border-color: #ef4444;
                        color: #ffffff;
                    }
                    .pf-top-close-btn {
                        width: 32px;
                        height: 32px;
                        border-radius: 8px;
                        border: 1px solid rgba(255, 255, 255, 0.15);
                        background: rgba(255, 255, 255, 0.08);
                        color: #cbd5e1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        cursor: pointer;
                        transition: all 0.18s;
                    }
                    .pf-top-close-btn:hover {
                        background: #ef4444;
                        border-color: #ef4444;
                        color: #ffffff;
                    }

                    /* ──── Integrated Glassmorphism Navigation Tabs (Inside Dark Header) ──── */
                    .pf-top-tabs {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        padding: 8px 24px 12px;
                        background: rgba(9, 19, 34, 0.35);
                        border-top: 1px solid rgba(255, 255, 255, 0.08);
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                    .pf-top-tabs::-webkit-scrollbar {
                        height: 0;
                    }
                    .pf-top-tab-item {
                        display: inline-flex;
                        align-items: center;
                        gap: 7px;
                        padding: 7px 14px;
                        border: 1px solid transparent;
                        border-radius: 8px;
                        background: transparent;
                        color: #94a3b8;
                        font-size: 12.5px;
                        font-weight: 500;
                        cursor: pointer;
                        white-space: nowrap;
                        transition: all 0.18s ease;
                    }
                    .pf-top-tab-item:hover {
                        color: #f8fafc;
                        background: rgba(255, 255, 255, 0.08);
                    }
                    .pf-top-tab-item--active {
                        color: #ffffff !important;
                        font-weight: 600;
                        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
                        border-color: rgba(96, 165, 250, 0.4);
                        box-shadow: 0 3px 10px rgba(37, 99, 235, 0.35);
                    }
                    .pf-top-tab-icon {
                        font-size: 13.5px;
                        display: inline-flex;
                        align-items: center;
                    }

                    /* ──── Content Area (Soft Slate-Blue Background + 2-Column Bento Grid) ──── */
                    .pf-content {
                        flex: 1;
                        overflow-y: auto;
                        padding: 20px 24px 24px;
                        background: #e8eef5;
                        position: relative;
                    }
                    .pf-content::-webkit-scrollbar { width: 6px; }
                    .pf-content::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; }

                    /* 2-Column Bento Grid */
                    .pf-bento-grid {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 16px;
                    }

                    /* Section Header */
                    .pf-section { }
                    .pf-section-header {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        margin-bottom: 16px;
                        padding: 12px 18px;
                        background: #f8fafc;
                        border: 1px solid #cbd5e1;
                        border-radius: 12px;
                        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
                        font-size: 15px;
                        font-weight: 700;
                        color: #0f172a;
                    }
                    .pf-section-icon {
                        color: #2563eb;
                        font-size: 17px;
                    }
                    .pf-header-icon-box {
                        width: 40px; height: 40px; border-radius: 10px; background: #eff6ff;
                        border: 1px solid #dbeafe; display: flex; align-items: center; justify-content: center;
                        flex-shrink: 0;
                    }
                    .pf-section-title { font-size: 16px; font-weight: 700; color: #0f172a; line-height: 1.3; }
                    .pf-section-subtitle { font-size: 12.5px; color: #64748b; margin-top: 2px; font-weight: 400; }

                    /* Form Cards (Tinted Surface & Soft Inputs) */
                    .pf-info-card {
                        background: #ffffff;
                        border: 1px solid #cbd5e1;
                        border-radius: 14px;
                        box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
                        overflow: hidden;
                        transition: all 0.2s;
                        display: flex;
                        flex-direction: column;
                    }
                    .pf-info-card:hover {
                        border-color: #94a3b8;
                        box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
                    }
                    .pf-card-header-bar {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        padding: 11px 16px;
                        background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
                        border-bottom: 1px solid #cbd5e1;
                    }
                    .pf-card-icon-pill {
                        width: 30px; height: 30px; border-radius: 8px; display: flex;
                        align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0;
                    }
                    .pf-card-section-title { font-size: 13.5px; font-weight: 700; color: #0f172a; }
                    .pf-card-section-desc { font-size: 11.5px; color: #475569; }
                    .pf-card-body { padding: 14px 16px 2px; }
                    .pf-field-label { font-size: 12.5px; font-weight: 600; color: #334155; }

                    /* Tinted inputs inside cards to eliminate white-on-white glare */
                    .pf-info-card .ant-input-affix-wrapper,
                    .pf-info-card .ant-input,
                    .pf-info-card .ant-picker,
                    .pf-info-card .ant-select-selector {
                        background-color: #f8fafc !important;
                        border-color: #cbd5e1 !important;
                        border-radius: 8px !important;
                    }
                    .pf-info-card .ant-input-affix-wrapper input.ant-input {
                        background-color: transparent !important;
                    }
                    .pf-info-card .ant-input-affix-wrapper:hover,
                    .pf-info-card .ant-input:hover,
                    .pf-info-card .ant-picker:hover,
                    .pf-info-card .ant-select:hover .ant-select-selector {
                        border-color: #60a5fa !important;
                        background-color: #ffffff !important;
                    }
                    .pf-info-card .ant-input-affix-wrapper-focused,
                    .pf-info-card .ant-input:focus,
                    .pf-info-card .ant-picker-focused,
                    .pf-info-card .ant-select-focused .ant-select-selector {
                        background-color: #ffffff !important;
                        border-color: #2563eb !important;
                    }
                    .pf-info-card .ant-form-item {
                        margin-bottom: 12px;
                    }

                    .pf-action-bar {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 16px;
                        padding: 12px 18px;
                        background: #ffffff;
                        border: 1px solid #cbd5e1;
                        border-radius: 12px;
                        box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
                    }
                    .pf-submit-btn-premium {
                        height: 40px !important; padding: 0 24px !important; border-radius: 9px !important;
                        font-size: 13.5px !important; font-weight: 600 !important;
                        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
                        border: none !important; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
                        transition: all 0.2s !important;
                    }
                    .pf-submit-btn-premium:hover {
                        box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4) !important;
                        transform: translateY(-1px);
                    }

                    /* Subsection cards for tables & grids */
                    .pf-subsection {
                        margin-bottom: 16px;
                        background: #ffffff;
                        border: 1px solid #cbd5e1;
                        border-radius: 12px;
                        padding: 16px 20px;
                        box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
                    }
                    .pf-subsection-title {
                        font-size: 14px;
                        font-weight: 600;
                        color: #1e293b;
                        margin-bottom: 12px;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }

                    .pf-card-list { display: flex; flex-direction: column; gap: 12px; }
                    .pf-card { border: 1px solid #cbd5e1; border-radius: 12px; padding: 16px 20px; background: #fff; transition: all 0.2s; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); }
                    .pf-card:hover { box-shadow: 0 4px 12px rgba(15, 23, 42, 0.07); border-color: #94a3b8; }
                    .pf-card--warning { border-color: #fde68a; border-left: 4px solid #f59e0b; background: #fffbeb; }
                    .pf-card--expired { border-color: #fecaca; border-left: 4px solid #ef4444; background: #fef2f2; }
                    .pf-card--reward { border-left: 4px solid #10b981; background: #f0fdf4; }
                    .pf-card--discipline { border-left: 4px solid #ef4444; background: #fef2f2; }
                    .pf-card-top { display: flex; justify-content: space-between; align-items: center; }
                    .pf-card-title { font-weight: 600; font-size: 14px; margin-top: 4px; }
                    .pf-card-sub { font-size: 12px; color: #64748b; }
                    .pf-card-date { font-size: 12px; color: #94a3b8; }
                    .pf-card-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; color: #fff; margin-bottom: 6px; }
                    .pf-card-badge--warning { background: #f59e0b; }
                    .pf-card-badge--expired { background: #ef4444; }

                    .pf-leave-stats { display: flex; gap: 16px; margin-top: 10px; }
                    .pf-leave-stat { display: flex; flex-direction: column; align-items: center; flex: 1; padding: 12px; background: #f8fafc; border-radius: 10px; border: 1px solid #cbd5e1; }
                    .pf-leave-stat-label { font-size: 11px; color: #64748b; font-weight: 500; }
                    .pf-leave-stat-value { font-size: 22px; font-weight: 700; color: #0f172a; margin-top: 2px; }

                    .pf-loading { text-align: center; padding: 60px 0; }

                    /* ──── Responsive ──── */
                    @media (max-width: 900px) {
                        .pf-bento-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                    @media (max-width: 768px) {
                        .pf-layout { height: 90vh; }
                        .pf-top-header-main {
                            flex-wrap: wrap;
                            padding: 14px 16px;
                            gap: 12px;
                        }
                        .pf-top-actions {
                            width: 100%;
                            justify-content: flex-end;
                            border-top: 1px solid rgba(255, 255, 255, 0.08);
                            padding-top: 8px;
                        }
                        .pf-top-tabs { padding: 8px 12px 10px; }
                        .pf-top-tab-item { padding: 7px 12px; font-size: 12px; }
                        .pf-content { padding: 14px 12px 24px; }
                        .pf-leave-stats { flex-direction: row; gap: 8px; }
                        .pf-action-bar { flex-direction: column; gap: 10px; align-items: stretch; }
                    }
                `}</style>
            </Modal>

            {/* Password Modal */}
            <Modal
                title="Thay đổi mật khẩu"
                open={passwordModalOpen}
                onCancel={() => { setPasswordModalOpen(false); passForm.resetFields(); }}
                footer={null}
                width={400}
            >
                <Form form={passForm} layout="vertical" onFinish={handleChangePassword} style={{ marginTop: 24 }}>
                    <Form.Item name="newPassword" label="Mật khẩu mới" rules={[{ required: true, message: 'Vui lòng nhập mật khẩu mới' }, { min: 6, message: 'Tối thiểu 6 ký tự' }]}>
                        <Input.Password prefix={<LockOutlined />} size="large" />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label="Xác nhận mật khẩu" dependencies={['newPassword']}
                        rules={[{ required: true, message: 'Xác nhận mật khẩu' }, ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                return Promise.reject(new Error('Mật khẩu không khớp!'));
                            },
                        })]}>
                        <Input.Password prefix={<LockOutlined />} size="large" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" size="large" loading={loading} block danger style={{ marginTop: 12 }}>Đổi mật khẩu</Button>
                </Form>
            </Modal>

            {/* Add Contract Modal */}
            <Modal
                title="➕ Thêm hợp đồng lao động"
                open={addContractOpen}
                onCancel={() => { setAddContractOpen(false); contractForm.resetFields(); }}
                onOk={() => contractForm.submit()}
                confirmLoading={addModalLoading}
                okText="Lưu hợp đồng"
                cancelText="Hủy"
                width={520}
                destroyOnClose
            >
                <Form form={contractForm} layout="vertical" onFinish={handleAddContract} style={{ marginTop: 16 }}>
                    <Form.Item name="contract_number" label="Số hợp đồng" rules={[{ required: true, message: 'Nhập số hợp đồng' }]}>
                        <Input placeholder="VD: HĐ-2026-001" />
                    </Form.Item>
                    <Form.Item name="contract_type" label="Loại hợp đồng" rules={[{ required: true, message: 'Chọn loại hợp đồng' }]}>
                        <Select placeholder="Chọn loại hợp đồng">
                            <Select.Option value="1_MONTH">1 Tháng</Select.Option>
                            <Select.Option value="APPRENTICESHIP">Học việc</Select.Option>
                            <Select.Option value="PROBATION">Thử việc</Select.Option>
                            <Select.Option value="12_MONTHS">12 Tháng</Select.Option>
                            <Select.Option value="24_MONTHS">24 Tháng</Select.Option>
                            <Select.Option value="36_MONTHS">36 Tháng</Select.Option>
                            <Select.Option value="INDEFINITE">Không xác định</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="job_title" label="Chức danh">
                        <Input placeholder="VD: Nhân viên kỹ thuật" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Form.Item name="start_date" label="Ngày bắt đầu" rules={[{ required: true, message: 'Chọn ngày' }]}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                        <Form.Item name="end_date" label="Ngày kết thúc">
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                    </div>
                    <Form.Item name="status" label="Trạng thái" initialValue="ACTIVE">
                        <Select>
                            <Select.Option value="ACTIVE">Còn hiệu lực</Select.Option>
                            <Select.Option value="EXPIRED">Hết hạn</Select.Option>
                            <Select.Option value="TERMINATED">Chấm dứt</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add Reward/Discipline Modal */}
            <Modal
                title="➕ Thêm Khen thưởng / Kỷ luật"
                open={addRewardOpen}
                onCancel={() => { setAddRewardOpen(false); rewardForm.resetFields(); }}
                onOk={() => rewardForm.submit()}
                confirmLoading={addModalLoading}
                okText="Lưu quyết định"
                cancelText="Hủy"
                width={520}
                destroyOnClose
            >
                <Form form={rewardForm} layout="vertical" onFinish={handleAddReward} style={{ marginTop: 16 }}>
                    <Form.Item name="record_type" label="Loại" rules={[{ required: true, message: 'Chọn loại' }]}>
                        <Select placeholder="Chọn loại">
                            <Select.Option value="REWARD">🏆 Khen thưởng</Select.Option>
                            <Select.Option value="DISCIPLINE">⚠️ Kỷ luật</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="decision_number" label="Số quyết định" rules={[{ required: true, message: 'Nhập số quyết định' }]}>
                        <Input placeholder="VD: QĐ-2026-001" />
                    </Form.Item>
                    <Form.Item name="content" label="Nội dung" rules={[{ required: true, message: 'Nhập nội dung' }]}>
                        <Input.TextArea rows={3} placeholder="Mô tả nội dung khen thưởng / kỷ luật" />
                    </Form.Item>
                    <Form.Item name="effective_date" label="Ngày hiệu lực" rules={[{ required: true, message: 'Chọn ngày' }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Ghi nhận Nghỉ phép / Nghỉ bù */}
            <Modal
                title={`🏖️ Ghi nhận Nghỉ phép / Nghỉ bù - ${user?.name || ''}`}
                open={recordLeaveOpen}
                onCancel={() => {
                    setRecordLeaveOpen(false);
                    setLeaveFileList([]);
                }}
                onOk={() => recordLeaveForm.submit()}
                confirmLoading={recordLeaveLoading}
                destroyOnClose
                width={540}
            >
                <Form form={recordLeaveForm} layout="vertical" onFinish={handleRecordLeaveSubmitInProfile} style={{ marginTop: 16 }}>
                    <Form.Item label="Nhân viên">
                        <Input value={`${user?.name} (${user?.employee_code || user?.user_id || ''})`} disabled />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col xs={24} sm={14}>
                            <Form.Item name="leave_type" label="Loại hình nghỉ" initialValue="ANNUAL" rules={[{ required: true }]}>
                                <Select>
                                    <Select.Option value="ANNUAL">🏖️ Nghỉ phép năm (Trừ quỹ phép)</Select.Option>
                                    <Select.Option value="COMPENSATORY">⏰ Nghỉ bù ca/lễ (Trừ quỹ bù)</Select.Option>
                                    <Select.Option value="SPECIAL_PAID">💍 Việc riêng có lương (Cưới hỏi, tang...)</Select.Option>
                                    <Select.Option value="SICK_LEAVE">🏥 Nghỉ ốm / Thai sản / BHXH</Select.Option>
                                    <Select.Option value="UNPAID">🚫 Nghỉ việc riêng không lương</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={10}>
                            <Form.Item name="days" label="Số ngày nghỉ" rules={[{ required: true, message: 'Nhập số ngày' }]}>
                                <InputNumber min={0.5} max={30} step={0.5} style={{ width: '100%' }} placeholder="VD: 1, 0.5..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="date_range" label="Thời gian nghỉ (Từ ngày - Đến ngày)" rules={[{ required: true, message: 'Chọn khoảng ngày' }]}>
                        <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" onChange={handleDateRangeChangeInProfile} />
                    </Form.Item>

                    <Form.Item name="reason" label="Lý do nghỉ phép">
                        <Input.TextArea rows={3} placeholder="Ghi chú thêm lý do..." />
                    </Form.Item>

                    <Form.Item label="Đính kèm file PDF nghỉ phép (Giấy đề nghị)">
                        <Upload {...leaveUploadProps}>
                            <Button icon={<UploadOutlined />}>Tải lên file PDF giấy đề nghị</Button>
                        </Upload>
                        <div style={{ color: '#8c8c8c', marginTop: 4, fontSize: 12 }}>
                            Hỗ trợ file PDF hoặc ảnh giấy phép đã duyệt (tối đa 10MB)
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ProfileModal;
